import { useEffect, useState, useMemo, Children } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import PilItPanel from "@/components/PilItPanel";
import { LineChart, Line, XAxis, YAxis, Tooltip as RTooltip, ResponsiveContainer } from "recharts";
import ForecastInsightDialog from "@/components/ForecastInsightDialog";
import MeetingWeekRow from "@/components/MeetingWeekRow";
import {
  Loader2, GanttChartSquare, Network, Map as MapIcon, Kanban, BarChart3, DollarSign, Users, GitCompareArrows,
  ShieldAlert, ClipboardList, Presentation, ShieldCheck, FolderOpen, IdCard, Plus, CalendarClock, NotebookPen, ChevronDown, ChevronRight,
  AlertTriangle, TrendingUp, TrendingDown, Minus, ArrowRight, Flag, CheckCircle2, GripVertical, Zap, HeartPulse, Sparkles, Timer, Circle, HelpCircle, Send, ListTodo, PieChart, CheckSquare, OctagonAlert, FileCheck2, Radar, Lightbulb, Layers, Wand2, X,
} from "lucide-react";

const TRACK_COLORS = { on_track: "#10B981", at_risk: "#F59E0B", jeopardy: "#EF4444" };

export default function DashboardView({ projectId, tasks, onNavTab, onAddTask, onOpenTrueup, onOpenTask, onStatOpen }) {
  const nav = useNavigate();
  const [projects, setProjects] = useState([]);
  const [selIds, setSelIds] = useState(projectId ? [projectId] : []);
  const [scopeOpen, setScopeOpen] = useState(false);
  const [autoOpen, setAutoOpen] = useState(false);
  const [datas, setDatas] = useState(null);
  const [addOpen, setAddOpen] = useState(null); // "action" | "risk"
  const [wizardTipHidden, setWizardTipHidden] = useState(() => localStorage.getItem("pmo_wizard_tip_hidden") === "1");
  const [todoOpen, setTodoOpen] = useState(false);
  const [calWeek, setCalWeek] = useState([]);
  useEffect(() => {
    if (projectId) return;
    const today = new Date();
    const s = today.toISOString().slice(0, 10);
    const e = new Date(today.getTime() + 6 * 86400000).toISOString().slice(0, 10);
    api.get(`/calendar/events?start=${s}&end=${e}`)
      .then(({ data }) => setCalWeek((data.events || []).filter((ev) => ev.kind === "outlook").sort((a, b) => (a.date + a.time).localeCompare(b.date + b.time))))
      .catch(() => {});
  }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps
  const [listOpen, setListOpen] = useState(null);
  const [taskDrill, setTaskDrill] = useState(null);
  const [drillTasks, setDrillTasks] = useState(null);
  const [trendSeries, setTrendSeries] = useState(null);
  const [radarWeek, setRadarWeek] = useState([]);
  const [insightTask, setInsightTask] = useState(null);
  useEffect(() => {
    if (projectId) return;
    api.get("/radar")
      .then(({ data }) => setRadarWeek((data.items || []).filter((it) => it.due || it.due_soon)))
      .catch(() => {});
  }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps
  const [nudgeCard, setNudgeCard] = useState(null);
  const [trendOpen, setTrendOpen] = useState(false);
  const [glanceOpen, setGlanceOpen] = useState(() => localStorage.getItem("dash-glance-open") !== "0");
  const toggleGlance = () => setGlanceOpen((o) => { localStorage.setItem("dash-glance-open", o ? "0" : "1"); return !o; });
  const [stackOpen, setStackOpen] = useState(() => localStorage.getItem("dash-stack-open") !== "0");
  const toggleStack = () => setStackOpen((o) => { localStorage.setItem("dash-stack-open", o ? "0" : "1"); return !o; });
  const [stackGroup, setStackGroup] = useState(() => localStorage.getItem("dash-stack-group") || "none");
  const toggleStackGroup = () => setStackGroup((g) => { const n = g === "none" ? "health" : g === "health" ? "program" : "none"; localStorage.setItem("dash-stack-group", n); return n; });
  const [archSuggest, setArchSuggest] = useState([]);
  const dismissedArch = () => { try { return JSON.parse(localStorage.getItem("archive-suggest-dismissed")) || []; } catch { return []; } };
  useEffect(() => {
    if (projectId) return;
    api.get("/projects/archive-suggestions")
      .then(({ data }) => setArchSuggest(data.filter((s) => !dismissedArch().includes(s.id))))
      .catch(() => {});
  }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps
  const dismissArchSuggest = (id) => {
    localStorage.setItem("archive-suggest-dismissed", JSON.stringify([...dismissedArch(), id]));
    setArchSuggest((l) => l.filter((s) => s.id !== id));
  };
  const archiveSuggested = async (s) => {
    try {
      await api.post(`/projects/${s.id}/archive`);
      toast.success(`"${s.name}" archived — find it under Library → Archived Projects`, {
        action: { label: "Undo", onClick: async () => {
          try {
            await api.post(`/projects/${s.id}/unarchive`);
            toast.success(`"${s.name}" restored`);
            api.get("/projects").then(({ data }) => setProjects(data)).catch(() => {});
          } catch { toast.error("Could not restore"); }
        } },
      });
      setArchSuggest((l) => l.filter((x) => x.id !== s.id));
      api.get("/projects").then(({ data }) => setProjects(data)).catch(() => {});
    } catch { toast.error("Could not archive project"); }
  };
  const [healthContent, setHealthContent] = useState(null);
  const [autoInfoOpen, setAutoInfoOpen] = useState(false);

  useEffect(() => { api.get("/projects").then(({ data }) => setProjects(data)).catch(() => {}); }, []);
  useEffect(() => { setSelIds(projects.length ? projects.map((p) => p.id) : (projectId ? [projectId] : [])); }, [projectId, projects]);

  const load = async () => {
    const ids = selIds.filter(Boolean);
    if (!ids.length) return;
    try {
      const entries = await Promise.all(ids.map(async (pid) => {
        const [dash, raidRes, approvals] = await Promise.all([
          api.get(`/projects/${pid}/dashboard`).then((r) => r.data),
          api.get(`/projects/${pid}/raid`).then((r) => r.data).catch(() => null),
          api.get(`/projects/${pid}/approvals`).then((r) => r.data).catch(() => []),
        ]);
        return [pid, { dash, raid: raidRes?.summary || null, raidFull: raidRes, approvals }];
      }));
      setDatas(Object.fromEntries(entries));
    } catch (e) { toast.error("Could not load dashboard"); }
  };
  useEffect(() => { load(); }, [projectId, tasks, selIds]); // eslint-disable-line react-hooks/exhaustive-deps

  const list = selIds.map((id) => datas?.[id]).filter(Boolean);
  // task drill: fetch full task lists when a status legend item is clicked
  useEffect(() => {
    if (!taskDrill) { setDrillTasks(null); return; }
    (async () => {
      const out = [];
      await Promise.all(selIds.map(async (pid) => {
        try {
          const { data } = await api.get(`/projects/${pid}/tasks`);
          const pname = multi ? (projects.find((p) => p.id === pid)?.name || "") : "";
          data.filter((t) => !t.is_summary).forEach((t) => {
            const isDone = t.status === "done" || (t.percent_complete || 0) >= 100;
            const ns = !isDone && (t.percent_complete || 0) <= 0 && t.status !== "in_progress";
            const bucket = isDone ? "done" : ns ? "not_started" : "active";
            if (bucket === taskDrill) out.push({ ...t, _pname: pname, _pid: pid });
          });
        } catch {}
      }));
      out.sort((a, b) => (a.end_date || "9999").localeCompare(b.end_date || "9999"));
      setDrillTasks(out);
    })();
  }, [taskDrill]); // eslint-disable-line react-hooks/exhaustive-deps

  // weekly progress trend per selected project
  useEffect(() => {
    let alive = true;
    (async () => {
      const ids = selIds.slice(0, 5);
      const out = await Promise.all(ids.map(async (pid) => {
        try {
          const { data } = await api.get(`/projects/${pid}/progress-history`);
          return { id: pid, name: projects.find((x) => x.id === pid)?.name || "Project", ...data };
        } catch { return null; }
      }));
      if (alive) setTrendSeries(out.filter(Boolean));
    })();
    return () => { alive = false; };
  }, [selIds, projects]);

  const taskDrillContent = taskDrill ? {
    title: { done: "Completed tasks", active: "Active tasks", not_started: "Tasks not started" }[taskDrill],
    subtitle: drillTasks ? `${drillTasks.length} task${drillTasks.length === 1 ? "" : "s"}` : "Loading…",
    empty: drillTasks ? "No tasks in this bucket." : "Loading…",
    rows: (drillTasks || []).map((t) => ({
      key: `${t._pid}-${t.id}`, title: t.title,
      meta: [t._pname, t.assignee || t.responsible || "Unassigned", t.end_date ? `due ${t.end_date}` : null].filter(Boolean).join(" · "),
      badge: taskDrill === "done" ? "Done" : `${Math.round(t.percent_complete || 0)}%`,
      badgeClass: taskDrill === "done" ? "bg-blue-100 text-blue-700" : taskDrill === "active" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600",
      onClick: () => { setTaskDrill(null); if (t._pid === projectId) openTaskById(t.id); else goTab(t._pid, "plan"); },
    })),
  } : null;


  if (!datas || list.length === 0) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  const multi = selIds.length > 1;
  const data = datas[projectId]?.dash || list[0].dash;

  const openTaskById = (id) => {
    const t = (tasks || []).find((x) => x.id === id);
    if (t) onOpenTask?.(t);
  };

  const sum = (fn) => list.reduce((a, x) => a + (fn(x) || 0), 0);
  const raidAgg = list.reduce((acc, x) => {
    if (x.raid) Object.keys(x.raid).forEach((k) => { acc[k] = (acc[k] || 0) + x.raid[k]; });
    return acc;
  }, {});
  const nextDues = list.map((x) => x.dash.trueup?.next_due_at).filter(Boolean).sort();
  const agg = {
    pct: Math.round(sum((x) => x.dash.progress?.avg_percent) / list.length),
    dueWeek: sum((x) => (x.dash.upcoming_tasks || []).length),
    overdue: sum((x) => (x.dash.overdue_tasks || []).length),
    atRisk: sum((x) => (x.dash.flagged_tasks || []).length),
    cardsOpen: sum((x) => (x.dash.cards || []).length),
    cardsOverdue: sum((x) => (x.dash.cards || []).filter((c) => c.overdue > 0).length),
    auditScore: Math.round(sum((x) => x.dash.audit?.score) / list.length),
    auditFailing: sum((x) => (x.dash.audit?.failing || []).length),
    totalTasks: sum((x) => x.dash.progress?.total),
    doneTasks: sum((x) => x.dash.progress?.done),
    notStarted: sum((x) => x.dash.progress?.not_started),
    critical: sum((x) => x.dash.progress?.critical),
    raid: raidAgg,
    trueupDue: list.some((x) => x.dash.trueup?.is_due),
    trueupPending: sum((x) => x.dash.trueup?.pending_count),
    trueupSubmitted: sum((x) => x.dash.trueup?.submitted_count),
    trueupNext: nextDues[0] || null,
    autoShifts: sum((x) => x.dash.automation_stats?.shifts),
    autoNudges: Math.max(...list.map((x) => x.dash.automation_stats?.nudges || 0)),
    autoCards: Math.max(...list.map((x) => x.dash.automation_stats?.cards || 0)),
  };
  agg.autoMinutes = agg.autoShifts * 4 + agg.autoNudges * 10 + agg.autoCards * 12;
  agg.activeTasks = Math.max(agg.totalTasks - agg.doneTasks - agg.notStarted, 0);

  const resourceBars = (() => {
    const pace = {};
    list.forEach((x) => (x.dash.resource_pace || []).forEach((r) => {
      const k = (r.person || "").toLowerCase();
      if (r.trend === "behind" || pace[k] === "behind") pace[k] = "behind";
      else pace[k] = pace[k] || r.trend;
    }));
    const m = {};
    list.forEach((x) => (x.dash.cards || []).forEach((c) => {
      const k = c.person;
      if (!m[k]) m[k] = { person: k, v: 0, actions: 0, overdue: 0, email: false };
      m[k].v += c.open_tasks || 0;
      m[k].actions += c.open_actions || 0;
      m[k].overdue += c.overdue || 0;
      m[k].email = m[k].email || !!c.email;
    }));
    return Object.values(m).map((d) => {
      const behind = pace[d.person.toLowerCase()] === "behind";
      const rank = (d.overdue >= 2 || behind) ? 0 : d.overdue === 1 ? 1 : 2;
      const color = rank === 0 ? "#EF4444" : rank === 1 ? "#EAB308" : "#10B981";
      return { ...d, label: d.person.split(" ")[0], color, rank };
    }).sort((a, b) => (a.rank - b.rank) || (b.v - a.v));
  })();
  agg.closedActions = sum((x) => ((x.raidFull?.actions) || []).filter((a) => a.status === "done").length);
  const actionBars = [
    { label: "Late", v: agg.raid.late_actions || 0, color: "#EF4444" },
    { label: "On track", v: Math.max((agg.raid.open_actions || 0) - (agg.raid.late_actions || 0), 0), color: "#10B981" },
    { label: "Closed", v: agg.closedActions, color: "#2563EB" },
  ];
  const prevVals = list.map((x) => x.dash.progress?.last_week_percent).filter((v) => v != null);
  const weekTrend = prevVals.length ? { delta: agg.pct - Math.round(prevVals.reduce((a, b) => a + b, 0) / prevVals.length) } : null;

  const perStats = selIds.map((pid) => {
    const d = datas?.[pid]?.dash;
    if (!d) return null;
    return {
      name: projects.find((p) => p.id === pid)?.name || "Project",
      total: d.progress?.total || 0, done: d.progress?.done || 0,
      critical: d.progress?.critical || 0, atRisk: (d.flagged_tasks || []).length,
    };
  }).filter(Boolean);

  const toggleSel = (id) => setSelIds((s) => s.includes(id) ? (s.length > 1 ? s.filter((x) => x !== id) : s) : [...s, id]);
  const pickProject = (id) => setSelIds((s) => (s.length === 1 && s[0] === id) ? (projects.length ? projects.map((p) => p.id) : [projectId]) : [id]);
  const kpiSingle = !multi && selIds[0] === projectId;

  const toggleAutoPilot = async (pid, on) => {
    try {
      await api.put(`/projects/${pid}/pilit`, { enabled: on });
      setProjects((ps) => ps.map((p) => p.id === pid ? { ...p, update_mode: on ? "pilit" : "manual" } : p));
      load();
      toast.success(`Auto Pilot ${on ? "enabled" : "turned off"}`);
    } catch { toast.error("Could not update Auto Pilot"); }
  };

  const navTab = (t) => {
    if (onNavTab) return onNavTab(t);
    if (selIds.length === 1 && selIds[0]) return nav(`/projects/${selIds[0]}?tab=${t}`);
    if (t === "plan") setTodoOpen(true);
    else if (t === "raid") setListOpen("risks");
    else if (t === "audit") setListOpen("audit");
    else nav("/cards");
  };

  const openHealth = async (p) => {
    let dash = datas?.[p.id]?.dash;
    if (!dash) {
      try { dash = (await api.get(`/projects/${p.id}/dashboard`)).data; } catch { dash = null; }
    }
    const meta = { on_track: "On track", at_risk: "At risk", off_track: "Off track", completed: "Completed" }[p.health] || "On track";
    const go = () => { setHealthContent(null); if (p.id === projectId) onNavTab?.("plan"); else nav(`/projects/${p.id}?tab=plan`); };
    const seen = new Set();
    const rows = [];
    (dash?.overdue_tasks || []).forEach((t) => {
      seen.add(t.id);
      rows.push({ key: `od-${t.id}`, title: t.title, meta: `${t.assignee || "Unassigned"} · ${t.percent_complete}% done`, badge: `Overdue${t.end_date ? ` · ${t.end_date}` : ""}`, badgeClass: "bg-red-100 text-red-600", onClick: go });
    });
    (dash?.flagged_tasks || []).forEach((t) => {
      if (seen.has(t.id)) return;
      rows.push({ key: `fl-${t.id}`, title: t.title, meta: `${t.assignee || "Unassigned"} · ${t.percent_complete}% done`, badge: t.track_status === "jeopardy" ? "Jeopardy" : "At risk", badgeClass: t.track_status === "jeopardy" ? "bg-red-100 text-red-600" : "bg-amber-100 text-amber-700", onClick: go });
    });
    setHealthContent({
      title: `${p.name} — ${meta}`,
      subtitle: rows.length ? `What's driving this status (${rows.length} item${rows.length === 1 ? "" : "s"})` : null,
      empty: p.health === "completed" ? "All tasks are complete." : p.health === "on_track" ? "Nothing overdue or flagged — this project is on track." : "Couldn't load the task details for this project.",
      rows,
    });
  };

  const todoItems = selIds.flatMap((pid) => {
    const d = datas?.[pid]?.dash;
    if (!d) return [];
    const pname = projects.find((p) => p.id === pid)?.name || "";
    return [
      ...(d.overdue_tasks || []).map((t) => ({ ...t, kind: "overdue", project_id: pid, project_name: pname })),
      ...(d.upcoming_tasks || []).map((t) => ({ ...t, kind: "due", project_id: pid, project_name: pname })),
    ];
  }).sort((a, b) => (a.kind === b.kind ? (a.end_date || "9999") < (b.end_date || "9999") ? -1 : 1 : a.kind === "overdue" ? -1 : 1));

  const openTodoItem = (t) => {
    setTodoOpen(false);
    if (t.project_id === projectId) openTaskById(t.id);
    else nav(`/projects/${t.project_id}?tab=plan`);
  };

  const goTab = (pid, tab) => {
    setListOpen(null);
    if (pid === projectId) onNavTab?.(tab);
    else nav(`/projects/${pid}?tab=${tab}`);
  };

  const onGlanceBullet = (kind) => {
    if (kind === "tasks-due") setTodoOpen(true);
    else if (kind === "stakeholders") navTab("stakeholders");
    else setListOpen(kind);
  };

  const buildGlanceList = (kind) => {
    const todayIso = new Date().toISOString().slice(0, 10);
    const per = (fn) => selIds.flatMap((pid) => {
      const e = datas?.[pid];
      if (!e) return [];
      const pname = multi ? (projects.find((p) => p.id === pid)?.name || "") : "";
      return fn(e, pid, pname);
    });
    const withP = (meta, pname) => [meta, pname].filter(Boolean).join(" · ");
    if (kind === "cards") return {
      title: "People with open items",
      subtitle: `${agg.cardsOpen} people${agg.cardsOverdue ? ` · ${agg.cardsOverdue} with overdue work` : ""}`,
      empty: "No one has open items right now.",
      rows: per((e, pid, pname) => (e.dash.cards || []).map((c) => ({
        key: `${pid}-${c.key}`, title: c.person,
        meta: withP(`${c.open_tasks} task${c.open_tasks === 1 ? "" : "s"} · ${c.open_actions} action${c.open_actions === 1 ? "" : "s"}`, pname),
        badge: c.overdue > 0 ? `${c.overdue} overdue` : "On track",
        badgeClass: c.overdue > 0 ? "bg-red-100 text-red-600" : "bg-emerald-100 text-emerald-700",
        onClick: () => goTab(pid, "cards"),
      }))),
    };
    if (kind === "risks") return {
      title: "Open risks & issues",
      subtitle: `${agg.raid.open_risks || 0} risks · ${agg.raid.open_issues || 0} issues`,
      empty: "No open risks or issues.",
      rows: per((e, pid, pname) => [...(e.raidFull?.risks || []), ...(e.raidFull?.issues || [])]
        .filter((r) => r.status !== "closed")
        .map((r) => {
          const score = r.score || (r.probability || 3) * (r.impact || 3);
          return {
            key: r.id, title: r.title, score,
            meta: withP(r.owner || "Unassigned", pname),
            badge: `${r.kind === "issue" ? "Issue" : "Risk"} · P×I ${score}`,
            badgeClass: r.kind === "issue" ? "bg-red-100 text-red-600" : "bg-amber-100 text-amber-700",
            onClick: () => goTab(pid, "raid"),
          };
        })).sort((a, b) => b.score - a.score),
    };
    if (kind === "actions-late") {
      const lateRows = per((e, pid, pname) => (e.raidFull?.actions || [])
        .filter((a) => a.status !== "done" && a.due_date && a.due_date < todayIso)
        .map((a) => ({
          key: a.id, title: a.title,
          meta: withP(a.owner || "Unassigned", pname),
          badge: `Late · ${a.due_date}`, badgeClass: "bg-red-100 text-red-600",
          onClick: () => goTab(pid, "raid"),
        })));
      return {
        title: "Late action items",
        subtitle: `${lateRows.length} late — owners need a nudge`,
        empty: "Nothing is running late.",
        rows: lateRows,
        action: lateRows.length ? {
          label: "Email follow-ups to owners",
          fn: async () => {
            const pids = selIds.filter((pid) => ((datas?.[pid]?.raidFull?.actions) || []).some((a) => a.status !== "done" && a.due_date && a.due_date < todayIso));
            let sent = 0; const unresolved = [];
            for (const pid of pids) {
              try {
                const { data } = await api.post(`/projects/${pid}/action-items/follow-up?only_late=true`);
                sent += data.sent || 0;
                unresolved.push(...(data.unresolved || []));
              } catch { /* skip project */ }
            }
            const uniq = [...new Set(unresolved)];
            if (sent) toast.success(`Follow-up emails sent to ${sent} owner(s)${uniq.length ? ` — no email found for: ${uniq.join(", ")}` : ""}`, { duration: 8000 });
            else toast.error(uniq.length ? `No email address found for: ${uniq.join(", ")}` : "No follow-ups could be sent");
          },
        } : null,
      };
    }
    if (kind === "actions-pending" || kind === "actions-done") {
      const wantDone = kind === "actions-done";
      return {
        title: wantDone ? "Completed action items" : "Pending action items",
        subtitle: wantDone ? "Everything already closed out" : "Open items that are not late",
        empty: wantDone ? "Nothing completed yet." : "No pending action items.",
        rows: per((e, pid, pname) => (e.raidFull?.actions || [])
          .filter((a) => wantDone ? a.status === "done" : (a.status !== "done" && !(a.due_date && a.due_date < todayIso)))
          .map((a) => ({
            key: a.id, title: a.title,
            meta: withP(a.owner || "Unassigned", pname),
            badge: wantDone ? "Done" : (a.due_date ? `Due ${a.due_date}` : "No due date"),
            badgeClass: wantDone ? "bg-blue-100 text-blue-700" : "bg-emerald-100 text-emerald-700",
            onClick: () => goTab(pid, "raid"),
          }))),
      };
    }
    if (kind === "actions") return {
      title: "Open action items",
      subtitle: `${agg.raid.open_actions || 0} open${agg.raid.late_actions ? ` · ${agg.raid.late_actions} late` : ""}`,
      empty: "No open action items.",
      rows: per((e, pid, pname) => (e.raidFull?.actions || []).filter((a) => a.status !== "done").map((a) => {
        const late = !!(a.due_date && a.due_date < todayIso);
        return {
          key: a.id, title: a.title, late,
          meta: withP(a.owner || "Unassigned", pname),
          badge: a.due_date ? (late ? `Late · ${a.due_date}` : `Due ${a.due_date}`) : "No due date",
          badgeClass: late ? "bg-red-100 text-red-600" : "bg-blue-100 text-blue-700",
          onClick: () => goTab(pid, "raid"),
        };
      })).sort((a, b) => (a.late === b.late ? 0 : a.late ? -1 : 1)),
    };
    if (kind === "decisions") return {
      title: "Decisions to make",
      subtitle: `${agg.raid.open_decisions || 0} open${agg.raid.blocking_decisions ? ` · ${agg.raid.blocking_decisions} blocking progress` : ""}`,
      empty: "No open decisions.",
      rows: per((e, pid, pname) => (e.raidFull?.decisions || []).filter((d) => d.status === "open").map((d) => ({
        key: d.id, title: d.title,
        meta: withP([d.owner, d.due_date ? `due ${d.due_date}` : null].filter(Boolean).join(" · ") || "Unassigned", pname),
        badge: d.blocking ? "Blocking" : "Open",
        badgeClass: d.blocking ? "bg-red-100 text-red-600" : "bg-violet-100 text-violet-700",
        onClick: () => goTab(pid, "raid"),
      }))),
    };
    if (kind === "audit") return {
      title: "Audit standards at risk",
      subtitle: `Compliance ${agg.auditScore}%`,
      empty: "All audit standards are being met.",
      rows: per((e, pid, pname) => (e.dash.audit?.failing || []).map((f) => ({
        key: `${pid}-${f.key}`, title: f.label,
        meta: withP(f.detail || "", pname),
        badge: "At risk", badgeClass: "bg-red-100 text-red-600",
        onClick: () => goTab(pid, "audit"),
      }))),
    };
    if (kind === "total") return {
      title: "Tasks by project",
      subtitle: `${agg.totalTasks} tasks across ${selIds.length} project${selIds.length === 1 ? "" : "s"}`,
      empty: "No tasks yet.",
      rows: per((e, pid) => {
        const pr = e.dash.progress || {};
        const name = projects.find((x) => x.id === pid)?.name || "Project";
        return [{
          key: pid, title: name,
          meta: `${(pr.total || 0) - (pr.done || 0)} open · ${pr.done || 0} done of ${pr.total || 0}`,
          badge: `${pr.avg_percent || 0}%`,
          badgeClass: "bg-blue-100 text-blue-700",
          onClick: () => goTab(pid, "plan"),
        }];
      }),
    };
    if (kind === "critical") return {
      title: "Critical-priority tasks",
      subtitle: `${agg.critical} open across ${selIds.length} project${selIds.length === 1 ? "" : "s"}`,
      empty: "No open critical-priority tasks.",
      rows: per((e, pid, pname) => (e.dash.critical_tasks || []).map((t) => ({
        key: `${pid}-${t.id}`, title: t.title,
        meta: withP(`${t.assignee || "Unassigned"} · ${t.percent_complete}% done`, pname),
        badge: t.end_date ? `Due ${t.end_date}` : "Critical",
        badgeClass: "bg-red-100 text-red-600",
        onClick: () => goTab(pid, "plan"),
      }))),
    };
    if (kind === "complete") return {
      title: "Completion by project",
      subtitle: `${agg.doneTasks} tasks done · ${agg.pct}% overall`,
      empty: "No projects selected.",
      rows: per((e, pid) => {
        const pr = e.dash.progress || {};
        const name = projects.find((x) => x.id === pid)?.name || "Project";
        const pct = pr.avg_percent || 0;
        return [{
          key: pid, title: name,
          meta: `${pr.done || 0} of ${pr.total || 0} tasks done`,
          badge: `${pct}%`,
          badgeClass: pct >= 100 ? "bg-emerald-100 text-emerald-700" : pct >= 50 ? "bg-blue-100 text-blue-700" : "bg-amber-100 text-amber-700",
          onClick: () => goTab(pid, "plan"),
        }];
      }),
    };
    if (kind === "status") return {
      title: "Task progress by project",
      subtitle: `${agg.doneTasks} closed · ${agg.activeTasks} active · ${agg.notStarted} not started`,
      empty: "No tasks yet.",
      rows: per((e, pid) => {
        const pr = e.dash.progress || {};
        const name = projects.find((x) => x.id === pid)?.name || "Project";
        const active = Math.max((pr.total || 0) - (pr.done || 0) - (pr.not_started || 0), 0);
        return [{
          key: pid, title: name,
          meta: `${pr.done || 0} closed · ${active} active · ${pr.not_started || 0} not started`,
          badge: `${pr.avg_percent || 0}%`,
          badgeClass: "bg-blue-100 text-blue-700",
          onClick: () => goTab(pid, "plan"),
        }];
      }),
    };

    if (kind === "health") {
      const seen = new Set();
      return {
        title: "Tasks needing attention",
        subtitle: `${agg.critical} critical · ${agg.atRisk} at risk`,
        empty: "Nothing critical or at risk — everything is on track.",
        rows: per((e, pid, pname) => {
          const out = [];
          (e.dash.critical_tasks || []).forEach((t) => {
            seen.add(t.id);
            out.push({ key: `${pid}-c-${t.id}`, title: t.title, meta: withP(`${t.assignee || "Unassigned"} · ${t.percent_complete}% done`, pname), badge: "Critical", badgeClass: "bg-red-100 text-red-600", onClick: () => goTab(pid, "plan") });
          });
          (e.dash.flagged_tasks || []).forEach((t) => {
            if (seen.has(t.id)) return;
            out.push({ key: `${pid}-f-${t.id}`, title: t.title, meta: withP(`${t.assignee || "Unassigned"} · ${t.percent_complete}% done`, pname), badge: t.track_status === "jeopardy" ? "Jeopardy" : "At risk", badgeClass: t.track_status === "jeopardy" ? "bg-red-100 text-red-600" : "bg-amber-100 text-amber-700", onClick: () => goTab(pid, "plan") });
          });
          return out;
        }),
      };
    }
    if (kind === "flagged") return {
      title: "Tasks at risk",
      subtitle: `${agg.atRisk} flagged across ${selIds.length} project${selIds.length === 1 ? "" : "s"}`,
      empty: "No tasks are flagged at risk.",
      rows: per((e, pid, pname) => (e.dash.flagged_tasks || []).map((t) => ({
        key: `${pid}-${t.id}`, title: t.title,
        meta: withP(`${t.assignee || "Unassigned"} · ${t.percent_complete}% done`, pname),
        badge: t.track_status === "jeopardy" ? "Jeopardy" : "At risk",
        badgeClass: t.track_status === "jeopardy" ? "bg-red-100 text-red-600" : "bg-amber-100 text-amber-700",
        onClick: () => goTab(pid, "plan"),
      }))),
    };
    if (kind === "trueup") {
      const submitted = (tasks || []).filter((t) => t.owner_submitted && !t.pm_confirmed);
      return {
        title: "True-up status",
        subtitle: agg.trueupDue
          ? `True-up due now · ${agg.trueupPending} task update${agg.trueupPending === 1 ? "" : "s"} awaiting confirmation`
          : `Next true-up ${agg.trueupNext ? new Date(agg.trueupNext).toLocaleDateString() : "not scheduled"}${agg.trueupSubmitted ? ` · ${agg.trueupSubmitted} owner update(s) to review` : ""}`,
        empty: "No owner-submitted updates waiting for review.",
        rows: submitted.map((t) => ({
          key: t.id, title: t.title,
          meta: `${t.assignee || t.responsible || "Unassigned"} · submitted ${t.proposed_percent ?? t.percent_complete ?? 0}%`,
          badge: "Confirm", badgeClass: "bg-amber-100 text-amber-700",
          onClick: () => { setListOpen(null); openTaskById(t.id); },
        })),
        action: (agg.trueupDue || agg.trueupSubmitted > 0) && onOpenTrueup ? { label: "Start true-up", fn: onOpenTrueup } : null,
      };
    }
    return null;
  };

  return (
    <div className="space-y-4 rounded-2xl p-5 sm:p-6 bg-white border border-border" data-testid="project-dashboard">
      {!projectId && (
        <div className="rounded-xl border border-border bg-secondary/40 px-4 py-3" data-testid="week-glance-strip">
          {todoItems.length > 0 && <>
          <div className="flex items-center justify-between gap-2 mb-2">
            <button onClick={toggleGlance} className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-widest text-muted-foreground hover:text-foreground transition-colors" data-testid="week-glance-toggle">
              {glanceOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
              Your week at a glance
              {!glanceOpen && <span className="normal-case tracking-normal font-semibold text-muted-foreground/80">· {todoItems.length} items</span>}
            </button>
            <button onClick={() => setTodoOpen(true)} className="text-xs font-bold text-primary hover:underline" data-testid="week-glance-viewall">View all {todoItems.length} →</button>
          </div>
          {glanceOpen && <div className="grid sm:grid-cols-3 gap-2">
            {todoItems.slice(0, 6).map((t) => (
              <button key={`${t.project_id}-${t.id}`} onClick={() => openTodoItem(t)} className="flex items-start gap-2 rounded-lg bg-white border border-border px-3 py-2 text-left hover:border-foreground/25 hover:shadow-sm transition-all group" data-testid={`week-glance-item-${t.id}`}>
                <span className={`shrink-0 text-[9px] font-bold px-1.5 py-0.5 rounded-full mt-0.5 ${t.kind === "overdue" ? "bg-red-800 text-white" : "bg-blue-100 text-blue-700"}`}>{t.kind === "overdue" ? "Overdue" : "Coming up"}</span>
                <span className="min-w-0">
                  <span className="block text-xs font-semibold truncate group-hover:text-primary transition-colors">{t.title}</span>
                  <span className="block text-[10px] text-muted-foreground truncate">{t.project_name}</span>
                </span>
              </button>
            ))}
          </div>}
          </>}
          <MeetingWeekRow projects={projects} divider={todoItems.length > 0} />
          {calWeek.length > 0 && (
            <div className={`flex items-center gap-2 flex-wrap ${todoItems.length > 0 ? "mt-2 pt-2 border-t border-border/70" : ""}`} data-testid="week-glance-calendar">
              <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground shrink-0">From your calendar</span>
              {calWeek.slice(0, 6).map((ev) => (
                <span key={ev.id} className="inline-flex items-center gap-1.5 rounded-full bg-white border border-border px-2.5 py-1 text-[11px] font-medium" title={`${ev.title}${ev.time ? ` · ${ev.time}` : ""}${ev.location ? ` · ${ev.location}` : ""}`} data-testid={`week-cal-event-${ev.id}`}>
                  <span className="h-1.5 w-1.5 rounded-full shrink-0" style={{ background: "#EA580C" }} />
                  <b>{new Date(ev.date + "T12:00:00").toLocaleDateString("en-US", { weekday: "short" })}</b>
                  {ev.time && <span className="text-muted-foreground">{ev.time}</span>}
                  <span className="truncate max-w-[180px]">{ev.title}</span>
                </span>
              ))}
              {calWeek.length > 6 && <span className="text-[10px] text-muted-foreground">+{calWeek.length - 6} more</span>}
            </div>
          )}
          {radarWeek.length > 0 && (
            <div className={`flex items-center gap-2 flex-wrap ${(todoItems.length > 0 || calWeek.length > 0) ? "mt-2 pt-2 border-t border-border/70" : ""}`} data-testid="week-glance-radar">
              <span className="text-[10px] font-bold uppercase tracking-widest text-sky-700 shrink-0 inline-flex items-center gap-1"><Radar className="h-3 w-3" /> On the radar</span>
              {radarWeek.slice(0, 6).map((it) => (
                <button key={`${it.type}-${it.id}`} onClick={() => nav(`/projects/${it.project_id}?tab=radar`)}
                  className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-medium hover:shadow-sm transition-all ${it.due ? "bg-red-50 border-red-300 text-red-800" : "bg-amber-50 border-amber-300 text-amber-800"}`}
                  title={`${it.title} — ${it.reason || "parked"} · revisit ${it.revisit}`} data-testid={`week-radar-item-${it.id}`}>
                  <b>{it.due ? "Revisit now" : `Revisit ${it.revisit}`}</b>
                  <span className="truncate max-w-[180px]">{it.title}</span>
                  <span className="opacity-70 truncate max-w-[110px]">· {it.project_name}</span>
                </button>
              ))}
              {radarWeek.length > 6 && <span className="text-[10px] text-muted-foreground">+{radarWeek.length - 6} more</span>}
            </div>
          )}
        </div>
      )}
      {/* Progress + scope + quick add */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex-1 min-w-[240px] flex items-center gap-3" data-testid="dashboard-overall-progress">
          <button type="button" onClick={() => nav("/dashboard")} title="Open the Projects page"
            className="text-xs font-semibold uppercase tracking-wide text-muted-foreground shrink-0 hover:text-primary hover:underline underline-offset-2 transition-colors inline-flex items-center gap-1"
            data-testid="dash-open-projects">
            {multi ? `${selIds.length} projects` : "Overall progress"} <ArrowRight className="h-3 w-3" />
          </button>
          <div className="h-2.5 flex-1 max-w-md rounded-full bg-secondary overflow-hidden">
            <div className="h-full rounded-full transition-all" style={{ width: `${agg.pct}%`, background: agg.pct >= 100 ? "#2563EB" : "#10B981" }} />
          </div>
          <span className="font-display font-extrabold text-lg tabular-nums" data-testid="dashboard-overall-pct">{agg.pct}%</span>
          {weekTrend && (
            <button type="button" onClick={() => setTrendOpen(true)} title="Week-over-week progress — click for history by project" className="inline-flex items-center hover:scale-110 transition-transform" data-testid="dash-week-trend-btn">
              {weekTrend.delta > 0
                ? <span className="inline-flex items-center gap-0.5 text-xs font-bold text-emerald-600" data-testid="dash-week-trend-up"><TrendingUp className="h-3.5 w-3.5" />+{weekTrend.delta}</span>
                : weekTrend.delta < 0
                  ? <span className="inline-flex items-center gap-0.5 text-xs font-bold text-red-600" data-testid="dash-week-trend-down"><TrendingDown className="h-3.5 w-3.5" />{weekTrend.delta}</span>
                  : <span className="h-1.5 w-4 rounded-full bg-yellow-400" data-testid="dash-week-trend-flat" />}
            </button>
          )}
        </div>
        <div className="flex gap-2 items-center">
          {onAddTask && <Button size="sm" className="gap-1.5" onClick={onAddTask} data-testid="dash-add-task"><Plus className="h-3.5 w-3.5" /> Task</Button>}
          {projectId && <Button size="sm" variant="outline" className="gap-1.5" onClick={() => setAddOpen("action")} data-testid="dash-add-action"><Plus className="h-3.5 w-3.5" /> Action</Button>}
          {projectId && <Button size="sm" variant="outline" className="gap-1.5" onClick={() => setAddOpen("risk")} data-testid="dash-add-risk"><Plus className="h-3.5 w-3.5" /> Risk</Button>}
          <Button size="sm" className="gap-1.5 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white border-0 shadow-[0_0_14px_rgba(245,158,11,0.45)]" onClick={() => nav("/build")} title="Plan Wizard — give it a start date or a deadline and the plan builds itself. Click to learn more & try it." data-testid="dash-plan-wizard">
            <Wand2 className="h-3.5 w-3.5" /> Plan wizard
            <span className="rounded-full bg-white text-orange-600 px-1.5 py-px text-[9px] font-extrabold tracking-wide">NEW!</span>
            <span className="text-[10px] font-semibold underline underline-offset-2 opacity-90">Learn more</span>
          </Button>
          <Button size="sm" variant="outline" className="gap-1.5" onClick={() => nav("/dashboard?new=1")} data-testid="dash-new-project"><FolderOpen className="h-3.5 w-3.5" /> New project</Button>
        </div>
      </div>

      {/* Plan Wizard promo strip */}
      {!wizardTipHidden && (
        <div className="rounded-xl border border-amber-300 bg-gradient-to-r from-amber-50 to-orange-50 px-4 py-2.5 flex items-center gap-3 flex-wrap" data-testid="dash-wizard-strip">
          <span className="h-7 w-7 rounded-lg bg-gradient-to-r from-amber-500 to-orange-500 text-white flex items-center justify-center shrink-0"><Wand2 className="h-4 w-4" /></span>
          <p className="text-sm min-w-0">
            <b className="font-display">Build a full plan with one date.</b>{" "}
            <span className="text-muted-foreground">Give the Plan Wizard a start date — or just your deadline — and it builds every task, duration and owner in seconds.</span>
          </p>
          <button type="button" onClick={() => nav("/build")} className="ml-auto text-sm font-bold text-orange-600 hover:text-orange-700 inline-flex items-center gap-1 shrink-0" data-testid="dash-wizard-strip-cta">Try it <ArrowRight className="h-3.5 w-3.5" /></button>
          <button type="button" onClick={() => { localStorage.setItem("pmo_wizard_tip_hidden", "1"); setWizardTipHidden(true); }} className="text-muted-foreground hover:text-foreground shrink-0" title="Dismiss" data-testid="dash-wizard-strip-dismiss"><X className="h-4 w-4" /></button>
        </div>
      )}

      {/* Compact Automation strip */}
      <div className="rounded-xl border-2 border-[#4d7dff] bg-[#0044FF] px-4 py-2 shadow-[0_0_18px_rgba(0,68,255,0.35)]" data-testid="dash-section-auto">
        <div className="flex items-center gap-2 flex-wrap">
          <HoverCard openDelay={150} closeDelay={150}>
            <HoverCardTrigger asChild>
              <button onClick={() => setAutoInfoOpen(true)} className="inline-flex items-center gap-1.5 cursor-help" data-testid="dash-auto-help-trigger">
                <Sparkles className="h-3.5 w-3.5 text-white" />
                <h2 className="text-[11px] font-bold uppercase tracking-widest text-white">Automation</h2>
                <HelpCircle className="h-3 w-3 text-white/70" />
              </button>
            </HoverCardTrigger>
            <HoverCardContent align="start" className="w-80" data-testid="dash-auto-hovercard">
              <p className="text-sm font-bold font-display mb-1.5">Your background project assistant</p>
              <ul className="text-xs text-muted-foreground space-y-1">
                <li>• <span className="font-semibold text-foreground">Auto Pilot</span> advances task progress at each task's expected pace and predicts finish dates.</li>
                <li>• <span className="font-semibold text-foreground">Nudges</span> automatically remind owners about due and overdue work.</li>
                <li>• <span className="font-semibold text-foreground">Cards</span> email each person their open items on your cadence — they update % without logging in.</li>
              </ul>
              <p className="text-xs text-muted-foreground mt-1.5">The timer shows PM time this saved you in the last 7 days.</p>
              <button onClick={() => setAutoInfoOpen(true)} className="mt-2 text-xs font-bold text-primary hover:underline inline-flex items-center gap-1" data-testid="dash-auto-learn-more">
                Learn more <ArrowRight className="h-3 w-3" />
              </button>
            </HoverCardContent>
          </HoverCard>
          <span className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider bg-orange-500 text-white px-1.5 py-0.5 rounded-full" data-testid="dash-auto-key-badge">Key feature</span>
          <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-white bg-white/15 border border-white/30 px-2 py-0.5 rounded-full" title={`Last 7 days: ${agg.autoShifts} tasks auto-rescheduled, ${agg.autoNudges} AI nudges, ${agg.autoCards} cards emailed`} data-testid="dash-auto-time-saved">
            <Timer className="h-3 w-3" />
            {agg.autoMinutes > 0
              ? `~${agg.autoMinutes >= 60 ? `${Math.floor(agg.autoMinutes / 60)}h ${agg.autoMinutes % 60}m` : `${agg.autoMinutes}m`} saved this week`
              : "Tracking time saved…"}
          </span>
          <div className="flex-1" />
          <Popover>
            <PopoverTrigger asChild>
              <button className="inline-flex items-center gap-1.5 text-[11px] font-bold text-white bg-white/15 border border-white/30 hover:bg-white/25 rounded-full px-2.5 py-0.5 transition-colors" data-testid="autopilot-menu-button">
                <Zap className="h-3 w-3" /> Auto Pilot on/off <ChevronDown className="h-3 w-3" />
              </button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-80" data-testid="autopilot-menu">
              <p className="text-sm font-bold mb-0.5">Auto Pilot per project</p>
              <p className="text-xs text-muted-foreground mb-2">Turn automatic progress nudges, forecasts and alerts on or off for each project.</p>
              <div className="space-y-1.5 max-h-64 overflow-y-auto">
                {projects.map((p) => (
                  <div key={p.id} className="flex items-center justify-between gap-2 rounded px-1 py-0.5 hover:bg-secondary/50" data-testid={`autopilot-row-${p.id}`}>
                    <span className="text-sm truncate flex-1">{p.name}</span>
                    <Switch checked={p.update_mode === "pilit"} onCheckedChange={(v) => toggleAutoPilot(p.id, v)} data-testid={`autopilot-switch-${p.id}`} />
                  </div>
                ))}
              </div>
            </PopoverContent>
          </Popover>
          {!multi && (
            <button onClick={() => setAutoOpen((v) => !v)} className="inline-flex items-center gap-1 text-[11px] font-bold text-white hover:underline" data-testid="dash-auto-expand">
              Auto Pilot &amp; forecasts {autoOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
            </button>
          )}
        </div>
        {autoOpen && !multi && (
          <div className="grid lg:grid-cols-3 gap-4 mt-3" data-testid="dash-auto-panels">
            <PilItPanel testid="dash-pilit" projectId={selIds[0]} pilit={data.pilit} onChanged={load} />
            <Panel title="Pilot forecast" icon={CalendarClock} accent="#A21CAF" count={(data.pilit?.forecast || []).length} testid="dash-forecast">
              {(data.pilit?.forecast || []).length === 0 ? <Empty text={data.pilit?.mode === "pilit" ? "Forecasts appear after the first Pilot run." : "Enable Pilot to get predicted finish dates."} /> : (
                (data.pilit.forecast || []).slice(0, 7).map((f) => (
                  <button key={f.task_id} onClick={() => (f.predicted_late ? setInsightTask(f) : openTaskById(f.task_id))}
                    title={f.predicted_late ? "Click for insights & tips to get this back on track" : "Open the task"}
                    className={`w-full flex items-center gap-2 py-1.5 px-1.5 text-left rounded ${f.predicted_late ? "hover:bg-red-50" : "hover:bg-secondary/40"}`} data-testid={`dash-forecast-${f.task_id}`}>
                    {f.predicted_late && <Lightbulb className="h-3.5 w-3.5 text-amber-500 shrink-0" data-testid={`forecast-insight-hint-${f.task_id}`} />}
                    <span className="text-sm truncate flex-1">{f.title}</span>
                    <span className="text-xs text-muted-foreground shrink-0">{f.percent_complete}%</span>
                    <span className={`text-xs shrink-0 font-semibold ${f.predicted_late ? "text-red-500" : "text-emerald-600"}`}>→ {f.predicted_finish}{f.predicted_late ? " late" : ""}</span>
                  </button>
                ))
              )}
            </Panel>
          </div>
        )}
      </div>

      {archSuggest.length > 0 && (
        <div className="flex flex-wrap items-center gap-2.5 rounded-xl border border-amber-300 bg-amber-50 px-4 py-2.5" data-testid="archive-suggest-banner">
          <Sparkles className="h-4 w-4 text-amber-600 shrink-0" />
          <p className="text-sm text-amber-900 flex-1 min-w-[220px]">
            <span className="font-bold">Auto Pilot:</span> <span className="font-semibold">{archSuggest[0].name}</span> is 100% done and hasn't moved in {archSuggest[0].days_idle} days — ready to archive?
            {archSuggest.length > 1 && <span className="text-amber-700"> (+{archSuggest.length - 1} more)</span>}
          </p>
          <Button size="sm" variant="outline" className="h-7 gap-1.5 border-amber-400 text-amber-800 hover:bg-amber-100" onClick={() => archiveSuggested(archSuggest[0])} data-testid="archive-suggest-yes">
            <CheckCircle2 className="h-3.5 w-3.5" /> Archive it
          </Button>
          <button onClick={() => dismissArchSuggest(archSuggest[0].id)} className="text-xs font-semibold text-amber-700 hover:underline" data-testid="archive-suggest-dismiss">Not now</button>
        </div>
      )}
      {/* Stat squares + project stack tiles (two rows) */}
      <div className="flex flex-wrap gap-4 items-end">
      <div className="flex flex-wrap gap-3" data-testid="dash-kpi-zone">
        <DashKpi label="Task Progression" value={agg.totalTasks} sub={`${agg.dueWeek} due this week · ${agg.overdue} overdue`}
          chart={
            <div className="flex items-center gap-3">
              <DonutCenter pct={agg.pct} trend={weekTrend} testid="kpi-donut-status" slices={[
                { label: "Closed", v: agg.doneTasks, color: "#2563EB" },
                { label: "Active", v: agg.activeTasks, color: "#10B981" },
                { label: "Not started", v: agg.notStarted, color: "#CBD5E1" },
              ]} />
              <Legend testid="kpi-legend-status" items={[
                { label: "Closed", count: agg.doneTasks, color: "#2563EB", onClick: () => setTaskDrill("done"), testid: "kpi-legend-closed" },
                { label: "Active", count: agg.activeTasks, color: "#10B981", onClick: () => setTaskDrill("active"), testid: "kpi-legend-active" },
                { label: "Not started", count: agg.notStarted, color: "#CBD5E1", onClick: () => setTaskDrill("not_started"), testid: "kpi-legend-notstarted" },
              ]} />
            </div>
          }
          icon={PieChart} accent="#2563EB" onClick={() => setListOpen("status")} testid="dash-kpi-status" />
        <DashKpi label="Action Items Progress" value={agg.raid.open_actions || 0} sub={agg.raid.late_actions ? `${agg.raid.late_actions} late — needs attention` : "nothing running late"}
          chart={(() => {
            const aiDone = agg.closedActions || 0;
            const aiLate = agg.raid.late_actions || 0;
            const aiPending = Math.max((agg.raid.open_actions || 0) - aiLate, 0);
            const aiTotal = aiDone + aiLate + aiPending;
            const aiPct = aiTotal ? Math.round((aiDone / aiTotal) * 100) : 0;
            return (
              <div className="flex items-center gap-3">
                <DonutCenter pct={aiPct} testid="kpi-donut-actions" slices={[
                  { label: "Late", v: aiLate, color: "#EF4444" },
                  { label: "Pending", v: aiPending, color: "#10B981" },
                  { label: "Completed", v: aiDone, color: "#2563EB" },
                ]} />
                <Legend testid="kpi-legend-actions" items={[
                  { label: "Late", count: aiLate, color: "#EF4444", onClick: () => setListOpen("actions-late"), testid: "kpi-legend-late" },
                  { label: "Pending", count: aiPending, color: "#10B981", onClick: () => setListOpen("actions-pending"), testid: "kpi-legend-pending" },
                  { label: "Completed", count: aiDone, color: "#2563EB", onClick: () => setListOpen("actions-done"), testid: "kpi-legend-completed" },
                ]} />
              </div>
            );
          })()}
          icon={ClipboardList} accent="#2563EB" onClick={() => setListOpen("actions")} testid="dash-kpi-actions" />
        <DashKpi label="Open Tasks by Resource" value={agg.cardsOpen} sub={`${agg.cardsOverdue ? `${agg.cardsOverdue} behind schedule · ` : ""}click a bar to nudge`}
          chart={
            <div>
              <LabeledBars data={resourceBars.slice(0, 6)} onBarClick={(d) => setNudgeCard(d)} testid="kpi-hist-resources" />
              <Legend inline testid="kpi-legend-resources" items={[
                { label: "On track", color: "#10B981" },
                { label: "At risk", color: "#EAB308" },
                { label: "Off track", color: "#EF4444" },
              ]} />
            </div>
          }
          icon={IdCard} accent="#7C3AED" onClick={() => setListOpen("cards")} testid="dash-kpi-resources" />
        <DashKpi label="Progress Trend" value={`${agg.pct}%`}
          sub={(() => {
            const single = trendSeries && trendSeries.length === 1 ? trendSeries[0] : null;
            if (single && single.slippage != null) return single.slippage < 0 ? `${Math.abs(single.slippage)}% behind plan` : single.slippage > 0 ? `${single.slippage}% ahead of plan` : "right on plan";
            return "weekly progress by project";
          })()}
          chart={<TrendMiniChart series={trendSeries} />}
          icon={TrendingUp} accent="#0EA5E9" onClick={() => setTrendOpen(true)} testid="dash-kpi-trend" />
        {(() => {
          const today = new Date().toISOString().slice(0, 10);
          const pend = list.flatMap((x) => (x.approvals || []).filter((a) => a.status === "pending"));
          const overdueAp = pend.filter((a) => a.due_date && a.due_date < today).length;
          const waiting = pend.reduce((n, a) => n + (a.approvers || []).filter((ap) => ap.status === "pending").length, 0);
          return (
            <DashKpi label="Document Approvals" value={pend.length}
              sub={pend.length ? `${waiting} approver${waiting === 1 ? "" : "s"} to respond${overdueAp ? ` · ${overdueAp} overdue` : ""}` : "nothing awaiting sign-off"}
              chart={
                <div className="space-y-1 max-w-[200px]">
                  {pend.slice(0, 3).map((a) => (
                    <div key={a.id} className="flex items-center gap-1.5 text-[11px]" data-testid={`dash-approval-${a.id}`}>
                      <span className="font-mono text-[9px] font-bold text-teal-700 shrink-0">{a.display_id}</span>
                      <span className="truncate text-muted-foreground">{a.title}</span>
                    </div>
                  ))}
                  {pend.length === 0 && <p className="text-[11px] text-muted-foreground">All documents signed off.</p>}
                  {pend.length > 3 && <p className="text-[10px] text-muted-foreground">+{pend.length - 3} more pending</p>}
                </div>
              }
              icon={FileCheck2} accent="#0D9488"
              onClick={() => nav(`/projects/${pend[0]?.project_id || selIds[0]}?tab=approvals`)}
              testid="dash-kpi-approvals" />
          );
        })()}
      </div>
      <div className="flex-1 min-w-[300px]" data-testid="dash-stack-tiles">
        <div className="flex items-center gap-2.5 mb-1.5">
          <button onClick={toggleStack} className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-muted-foreground hover:text-foreground transition-colors" data-testid="stack-toggle">
            {stackOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
            Project Stack
            {!stackOpen && <span className="normal-case tracking-normal font-semibold text-muted-foreground/80">· {projects.length} projects</span>}
          </button>
          {stackOpen && (
            <button onClick={toggleStackGroup} className={`inline-flex items-center gap-1 text-[10px] font-bold ${stackGroup !== "none" ? "text-primary" : "text-muted-foreground hover:text-foreground"}`} title="Group the project pills by health or program" data-testid="stack-group-toggle">
              <Layers className="h-3 w-3" /> {stackGroup === "health" ? "Grouped by health" : stackGroup === "program" ? "Grouped by program" : "Group pills"}
            </button>
          )}
          <div className="relative">
            <button onClick={() => setScopeOpen((v) => !v)} className="inline-flex items-center gap-1 text-[10px] font-bold text-primary hover:underline" data-testid="dash-scope-button">
              <FolderOpen className="h-3 w-3" /> {multi ? (selIds.length === projects.length ? "All projects" : `${selIds.length} selected`) : "1 selected"} <ChevronDown className="h-3 w-3" />
            </button>
            {scopeOpen && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setScopeOpen(false)} data-testid="dash-scope-overlay" />
                <div className="absolute left-0 top-full mt-1 z-50 w-72 bg-white border border-border rounded-lg shadow-xl p-2.5 space-y-1.5" data-testid="dash-scope-menu">
                  <span className="block px-1 text-[11px] font-bold uppercase tracking-wide text-muted-foreground">Zoom in on…</span>
                  <label className="flex items-center gap-2 text-sm font-semibold px-1 py-1 rounded hover:bg-secondary/50 cursor-pointer border-b border-border pb-1.5" data-testid="dash-scope-all">
                    <input type="checkbox" className="accent-primary" checked={projects.length > 0 && selIds.length === projects.length}
                      onChange={() => setSelIds(selIds.length === projects.length ? [projectId || projects[0]?.id].filter(Boolean) : projects.map((p) => p.id))} />
                    <span className="truncate">All projects</span>
                    <span className="ml-auto text-[10px] font-bold text-muted-foreground">{projects.length}</span>
                  </label>
                  <div className="max-h-56 overflow-y-auto space-y-1">
                    {projects.map((p) => (
                      <label key={p.id} className="flex items-center gap-2 text-sm px-1 py-0.5 rounded hover:bg-secondary/50 cursor-pointer" data-testid={`dash-scope-pick-${p.id}`}>
                        <input type="checkbox" className="accent-primary" checked={selIds.includes(p.id)} onChange={() => toggleSel(p.id)} />
                        <span className="truncate">{p.name}{p.id === projectId ? " (current)" : ""}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
        {stackOpen && (() => {
          const subset = selIds.length < projects.length;
          const renderPill = (p, i) => {
            const total = p.task_count ?? 0, done = p.done_count ?? 0;
            const pct = total ? Math.round((done / total) * 100) : 0;
            const doneProj = p.health === "completed";
            const isSel = selIds.includes(p.id);
            const hl = HEALTH_META[p.health]?.label || "On track";
            const active = selIds.length === 1 && selIds[0] === p.id;
            const HIcon = { on_track: CheckSquare, at_risk: AlertTriangle, off_track: OctagonAlert, completed: CheckCircle2 }[p.health] || CheckSquare;
            const hColor = { on_track: "#10B981", at_risk: "#F59E0B", off_track: "#EF4444", completed: "#94A3B8" }[p.health] || "#10B981";
            const selected = subset && isSel;
            return (
              <button key={p.id} onClick={() => pickProject(p.id)} onDoubleClick={() => nav(`/projects/${p.id}`)}
                title={`${active ? `Showing ${p.name} — click again to see all projects` : `Click to zoom stats to ${p.name}`} · double-click to open the project`}
                className={`group inline-flex items-center gap-1.5 h-7 pl-1 pr-2.5 rounded-full border text-xs font-semibold transition-all hover:shadow-sm hover:-translate-y-px max-w-[230px] ${doneProj ? "bg-slate-100 border-slate-200 text-slate-500" : selected ? "bg-primary border-primary text-white" : "bg-white border-border text-foreground hover:border-primary/50"}`}
                data-testid={`glance-phase-${i}`}>
                <span onClick={(e) => { e.stopPropagation(); openHealth(p); }}
                  title={`${hl} — click for details`}
                  className="shrink-0 h-5 w-5 rounded-full flex items-center justify-center cursor-pointer transition-transform hover:scale-110"
                  style={{ background: hColor }}
                  data-testid={`glance-health-${i}`}>
                  <HIcon className="h-3 w-3 text-white" strokeWidth={2.8} />
                </span>
                <span className="truncate">{p.name}</span>
                <span className={`shrink-0 text-[10px] font-extrabold tabular-nums ${selected ? "text-white/85" : "text-muted-foreground"}`}>{pct}%</span>
                {p.discipline != null && (
                  <span
                    title={`Input/Output discipline: ${p.discipline}%${p.discipline_trend != null ? ` (${p.discipline_trend > 0 ? "+" : ""}${p.discipline_trend} vs last week)` : ""} — share of phases that consume an input and promise an output milestone. Open the project's Flow view to close the gaps.`}
                    className={`shrink-0 inline-flex items-center gap-0.5 text-[9px] font-extrabold tabular-nums rounded-full px-1.5 py-px ${selected ? "bg-white/20 text-white" : p.discipline >= 80 ? "bg-emerald-100 text-emerald-700" : p.discipline >= 50 ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700"}`}
                    data-testid={`glance-discipline-${i}`}>
                    IO {p.discipline}
                    {p.discipline_trend != null && p.discipline_trend !== 0 && (
                      <span className={selected ? "text-white" : p.discipline_trend > 0 ? "text-emerald-600" : "text-red-600"} data-testid={`glance-discipline-trend-${i}`}>
                        {p.discipline_trend > 0 ? "▲" : "▼"}
                      </span>
                    )}
                  </span>
                )}
              </button>
            );
          };
          if (stackGroup === "program") {
            const names = [...new Set(projects.map((p) => p.program || "No program"))].sort((a, b) => (a === "No program") - (b === "No program") || a.localeCompare(b));
            return (
              <div className="space-y-1.5 pb-0.5">
                {names.map((nm) => {
                  const grp = projects.filter((p) => (p.program || "No program") === nm);
                  return (
                    <div key={nm} className="flex flex-wrap items-center gap-1.5" data-testid={`stack-program-${nm}`}>
                      <span className="text-[9px] font-bold uppercase tracking-widest text-primary/80 shrink-0 max-w-[140px] truncate" title={nm}>{nm} · {grp.length}</span>
                      {grp.map((p) => renderPill(p, projects.indexOf(p)))}
                    </div>
                  );
                })}
              </div>
            );
          }
          if (stackGroup === "health") {
            const order = [["off_track", "Off track", "#EF4444"], ["at_risk", "At risk", "#F59E0B"], ["on_track", "On track", "#10B981"], ["completed", "Completed", "#94A3B8"]];
            return (
              <div className="space-y-1.5 pb-0.5">
                {order.map(([k, label, color]) => {
                  const grp = projects.filter((p) => (p.health || "on_track") === k);
                  if (!grp.length) return null;
                  return (
                    <div key={k} className="flex flex-wrap items-center gap-1.5" data-testid={`stack-group-${k}`}>
                      <span className="text-[9px] font-bold uppercase tracking-widest w-[74px] shrink-0" style={{ color }}>{label} · {grp.length}</span>
                      {grp.map((p) => renderPill(p, projects.indexOf(p)))}
                    </div>
                  );
                })}
              </div>
            );
          }
          return <div className="flex flex-wrap items-center gap-1.5 pb-0.5">{projects.map((p, i) => renderPill(p, i))}</div>;
        })()}
      </div>
      </div>

      <ProjectGlance agg={agg} multi={multi} projects={projects} onNavTab={navTab} onOpenTrueup={onOpenTrueup} onOpenTodo={() => setTodoOpen(true)} onBullet={onGlanceBullet} />
      <ForecastInsightDialog projectId={selIds[0]} task={insightTask} onOpenChange={(v) => !v && setInsightTask(null)} onOpenTask={(tid) => { setInsightTask(null); openTaskById(tid); }} />
      <QuickAddDialog kind={addOpen} projectId={projectId} onOpenChange={(v) => !v && setAddOpen(null)} onCreated={() => { setAddOpen(null); load(); }} />
      <WeekTodoDialog open={todoOpen} onOpenChange={setTodoOpen} items={todoItems} multi={multi} onOpenItem={openTodoItem} onChanged={load} />
      {listOpen && <GlanceListDialog onOpenChange={setListOpen} content={buildGlanceList(listOpen)} />}
      {taskDrill && taskDrillContent && <GlanceListDialog onOpenChange={() => setTaskDrill(null)} content={taskDrillContent} />}
      <NudgeDialog card={nudgeCard} onOpenChange={(v) => !v && setNudgeCard(null)} onGoCards={() => { setNudgeCard(null); nav("/cards"); }} />
      <TrendHistoryDialog open={trendOpen} onOpenChange={setTrendOpen} projectIds={selIds} projects={projects} overallPct={agg.pct} overallDelta={weekTrend?.delta} />
      {healthContent && <GlanceListDialog onOpenChange={() => setHealthContent(null)} content={healthContent} />}
      <AutomationInfoDialog open={autoInfoOpen} onOpenChange={setAutoInfoOpen} />
    </div>
  );
}

function ReorderableGrid({ storageKey, className, children }) {
  const items = Children.toArray(children).filter(Boolean);
  const idOf = (c) => c.props?.testid || String(c.key);
  const [order, setOrder] = useState(() => {
    try { return JSON.parse(localStorage.getItem(storageKey)) || []; } catch { return []; }
  });
  const [dragId, setDragId] = useState(null);
  const sorted = [...items].sort((a, b) => {
    const ia = order.indexOf(idOf(a)), ib = order.indexOf(idOf(b));
    if (ia === -1 && ib === -1) return 0;
    if (ia === -1) return 1;
    if (ib === -1) return -1;
    return ia - ib;
  });
  const onDropOn = (targetId) => {
    if (!dragId || dragId === targetId) return;
    const ids = sorted.map(idOf);
    ids.splice(ids.indexOf(targetId), 0, ids.splice(ids.indexOf(dragId), 1)[0]);
    setOrder(ids);
    localStorage.setItem(storageKey, JSON.stringify(ids));
    setDragId(null);
  };
  return (
    <div className={className} data-testid="dash-reorderable-grid">
      {sorted.map((c) => {
        const id = idOf(c);
        return (
          <div key={id} className={`relative group/panel transition-opacity ${dragId === id ? "opacity-40" : ""} ${dragId && dragId !== id ? "outline-dashed outline-1 outline-border rounded-xl" : ""}`}
            onDragOver={(e) => { if (dragId) e.preventDefault(); }}
            onDrop={(e) => { e.preventDefault(); onDropOn(id); }}>
            <div draggable
              onDragStart={(e) => { e.dataTransfer.effectAllowed = "move"; setDragId(id); }}
              onDragEnd={() => setDragId(null)}
              className="absolute top-3.5 right-3 z-10 cursor-grab active:cursor-grabbing opacity-0 group-hover/panel:opacity-100 transition-opacity p-1 rounded hover:bg-secondary"
              title="Drag to reorder panels" data-testid={`drag-${id}`}>
              <GripVertical className="h-4 w-4 text-muted-foreground" />
            </div>
            {c}
          </div>
        );
      })}
    </div>
  );
}

function Section({ label, color, icon: Icon, testid, highlight, extra, children }) {
  return (
    <section data-testid={testid} className={highlight ? "rounded-2xl border-2 border-[#4d7dff] bg-[#0044FF] p-4 sm:p-5 shadow-[0_0_28px_rgba(0,68,255,0.45)]" : ""}>
      <div className="flex items-center gap-2 mb-2.5">
        <span className="h-5 w-5 rounded-md flex items-center justify-center" style={{ background: `${color}33` }}>
          <Icon className="h-3 w-3" style={{ color: "#FFFFFF" }} />
        </span>
        <h2 className={`text-[11px] font-bold uppercase tracking-widest ${highlight ? "text-white text-xs" : "text-slate-200"}`}>{label}</h2>
        {highlight && (
          <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-orange-500 text-white px-2 py-0.5 rounded-full" data-testid="dash-auto-key-badge">
            <Sparkles className="h-2.5 w-2.5" /> Key feature
          </span>
        )}
        {extra}
        <div className={`flex-1 h-px ${highlight ? "bg-orange-300/40" : "bg-white/15"}`} />
      </div>
      {children}
    </section>
  );
}

function Panel({ title, icon: Icon, count, badge, action, actionLabel, accent = "#64748B", testid, children }) {
  const [open, setOpen] = useState(() => localStorage.getItem(`dashpanel-${testid}`) !== "0");
  const toggle = () => setOpen((o) => { localStorage.setItem(`dashpanel-${testid}`, o ? "0" : "1"); return !o; });
  return (
    <div className="border border-border rounded-xl p-4 flex flex-col bg-card" style={{ borderTop: `3px solid ${accent}` }} data-testid={testid}>
      <div role="button" tabIndex={0} onClick={toggle} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggle(); } }} className="flex items-center gap-2 cursor-pointer select-none" data-testid={`${testid}-toggle`}>
        {open ? <ChevronDown className="h-3.5 w-3.5 text-muted-foreground shrink-0" /> : <ChevronRight className="h-3.5 w-3.5 text-muted-foreground shrink-0" />}
        <span className="h-6 w-6 rounded-md flex items-center justify-center shrink-0" style={{ background: `${accent}1a` }}>
          <Icon className="h-3.5 w-3.5" style={{ color: accent }} />
        </span>
        <h3 className="font-display font-bold text-sm flex-1 truncate">{title}</h3>
        {badge && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">{badge}</span>}
        {count != null && <span className="text-[11px] font-bold px-1.5 py-0.5 rounded-full tabular-nums" style={{ color: accent, background: `${accent}14` }}>{count}</span>}
        {action && (
          <button onClick={(e) => { e.stopPropagation(); action(); }} className="text-xs font-semibold inline-flex items-center gap-0.5 hover:underline shrink-0" style={{ color: accent }} data-testid={`${testid}-open`}>
            {actionLabel || "Open"} <ArrowRight className="h-3 w-3" />
          </button>
        )}
      </div>
      {open && <div className="flex-1 min-h-0 mt-2.5">{children}</div>}
    </div>
  );
}

function Empty({ text }) {
  return <p className="text-sm text-muted-foreground px-1.5 py-2">{text}</p>;
}

function DashKpi({ label, value, sub, chart, icon: Icon, accent, onClick, testid }) {
  return (
    <button type="button" onClick={onClick || undefined} disabled={!onClick}
      className={`w-full sm:w-[250px] min-h-[168px] rounded-2xl p-3.5 flex flex-col text-left transition-all bg-white border border-border shadow-sm ${onClick ? "hover:border-foreground/25 hover:shadow-md hover:-translate-y-0.5 cursor-pointer" : "cursor-default"}`}
      data-testid={testid}>
      <div className="flex items-center gap-2">
        <span className="h-7 w-7 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${accent}26` }}>
          <Icon className="h-3.5 w-3.5" strokeWidth={2.2} style={{ color: accent }} />
        </span>
        <p className="text-[11px] font-bold text-slate-700 truncate flex-1">{label}</p>
        {value !== undefined && <p className="font-display text-xl font-extrabold text-slate-900 tracking-tight">{value}</p>}
      </div>
      <div className="flex-1 flex flex-col justify-center mt-2">{chart}</div>
      {sub && <p className="text-[9px] text-muted-foreground leading-tight mt-1.5 line-clamp-1">{sub}</p>}
    </button>
  );
}

function TrendMiniChart({ series }) {
  const COLORS = ["#0044FF", "#10B981", "#F59E0B", "#8B5CF6", "#EF4444"];
  const { data, single } = useMemo(() => {
    if (!series || !series.length) return { data: [], single: null };
    const weekKey = (d) => {
      const dt = new Date(d + "T00:00:00");
      const mon = new Date(dt);
      mon.setDate(dt.getDate() - ((dt.getDay() + 6) % 7));
      return mon.toISOString().slice(0, 10);
    };
    const byWeek = {};
    series.forEach((s, i) => {
      (s.history || []).forEach((h) => {
        const w = weekKey(h.date);
        if (!byWeek[w]) byWeek[w] = { week: w };
        byWeek[w][`p${i}`] = h.avg_percent;
        if (series.length === 1 && h.planned_percent != null) byWeek[w].planned = h.planned_percent;
      });
    });
    const rows = Object.values(byWeek).sort((a, b) => a.week.localeCompare(b.week));
    return { data: rows, single: series.length === 1 ? series[0] : null };
  }, [series]);
  if (!data.length) return <p className="text-[11px] text-muted-foreground w-[210px]">Trend builds as daily progress snapshots accumulate.</p>;
  return (
    <div className="w-[230px]" data-testid="kpi-trend-chart">
      <ResponsiveContainer width="100%" height={84}>
        <LineChart data={data} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
          <XAxis dataKey="week" hide />
          <YAxis domain={[0, 100]} hide />
          <RTooltip formatter={(v, name) => [`${v}%`, name]} labelFormatter={(w) => `Week of ${w}`} contentStyle={{ fontSize: 11, padding: "4px 8px" }} />
          {single && <Line type="monotone" dataKey="planned" name="Planned" stroke="#94A3B8" strokeDasharray="4 3" strokeWidth={1.5} dot={false} connectNulls />}
          {(series || []).map((s, i) => (
            <Line key={s.id} type="monotone" dataKey={`p${i}`} name={s.name} stroke={COLORS[i % COLORS.length]} strokeWidth={2} dot={false} connectNulls />
          ))}
        </LineChart>
      </ResponsiveContainer>
      <div className="flex flex-wrap gap-x-2.5 gap-y-0.5">
        {(series || []).map((s, i) => (
          <span key={s.id} className="inline-flex items-center gap-1 text-[9px] text-slate-500 max-w-[105px]">
            <span className="h-1.5 w-1.5 rounded-full shrink-0" style={{ background: COLORS[i % COLORS.length] }} /><span className="truncate">{s.name}</span>
          </span>
        ))}
        {single && <span className="inline-flex items-center gap-1 text-[9px] text-slate-500"><span className="h-0.5 w-3 bg-slate-400" style={{ borderTop: "1.5px dashed #94A3B8" }} />Planned</span>}
      </div>
    </div>
  );
}

function DonutCenter({ slices, pct, trend, size = 88, testid }) {
  const total = slices.reduce((a, x) => a + x.v, 0);
  let acc = 0;
  return (
    <svg viewBox="0 0 42 42" style={{ height: size, width: size }} className="shrink-0" data-testid={testid}>
      <circle cx="21" cy="21" r="15.9155" fill="none" stroke="#F1F5F9" strokeWidth="6" />
      {total > 0 && slices.filter((x) => x.v > 0).map((x, i) => {
        const dash = (x.v / total) * 100;
        const off = 25 - acc;
        acc += dash;
        return (
          <circle key={i} cx="21" cy="21" r="15.9155" fill="none" stroke={x.color} strokeWidth="6"
            strokeDasharray={`${dash} ${100 - dash}`} strokeDashoffset={off} pathLength="100">
            <title>{`${x.label}: ${x.v}`}</title>
          </circle>
        );
      })}
      <text x="21" y="19.5" textAnchor="middle" fontSize="7.5" fontWeight="800" fill="#0F172A">{`${pct}%`}</text>
      <text x="21" y="23.5" textAnchor="middle" fontSize="2.9" fontWeight="700" fill="#64748B" letterSpacing="0.4">COMPLETE</text>
      {trend && (trend.delta > 0
        ? <text x="21" y="28.5" textAnchor="middle" fontSize="4" fontWeight="800" fill="#16A34A">{`▲ +${trend.delta} wk`}</text>
        : trend.delta < 0
          ? <text x="21" y="28.5" textAnchor="middle" fontSize="4" fontWeight="800" fill="#DC2626">{`▼ ${trend.delta} wk`}</text>
          : <rect x="17.5" y="26.5" width="7" height="1.6" rx="0.8" fill="#EAB308" />)}
    </svg>
  );
}

function Legend({ items, inline, testid }) {
  return (
    <div className={inline ? "flex flex-wrap gap-x-3 gap-y-0.5 mt-1" : "flex flex-col gap-1.5 min-w-0 flex-1"} data-testid={testid}>
      {items.map((it, i) => {
        return (
          <span key={i} onClick={it.onClick ? (e) => { e.stopPropagation(); it.onClick(); } : undefined}
            role={it.onClick ? "button" : undefined} tabIndex={it.onClick ? 0 : undefined}
            className={`flex items-center gap-1.5 text-[10px] text-slate-600 min-w-0 ${it.onClick ? "hover:underline cursor-pointer text-left w-full" : ""}`}
            data-testid={it.testid}>
            <span className="h-2 w-2 rounded-full shrink-0" style={{ background: it.color }} />
            <span className="truncate">{it.label}</span>
            {it.count !== undefined && <span className="ml-auto font-bold text-slate-800 tabular-nums pl-2">{it.count}</span>}
          </span>
        );
      })}
    </div>
  );
}

function LabeledBars({ data, height = 62, onBarClick, testid }) {
  if (!data.length) return <p className="text-[10px] text-muted-foreground py-4 text-center" data-testid={testid}>No data yet.</p>;
  const max = Math.max(...data.map((d) => d.v), 1);
  return (
    <div className="flex items-end gap-1.5" data-testid={testid}>
      {data.map((d, i) => (
        <div key={i} title={`${d.person || d.label}: ${d.v}${d.overdue ? ` · ${d.overdue} overdue` : ""}`}
          onClick={onBarClick ? (e) => { e.stopPropagation(); onBarClick(d); } : undefined}
          className={`flex-1 min-w-0 flex flex-col items-center justify-end ${onBarClick ? "cursor-pointer group" : ""}`}
          data-testid={`${testid}-bar-${i}`}>
          <span className="text-[9px] font-bold tabular-nums text-slate-700">{d.v}</span>
          <div className="w-full rounded-t-sm transition-all group-hover:brightness-110" style={{ height: Math.max((d.v / max) * height, d.v ? 8 : 3), background: d.v ? d.color : "#E2E8F0" }} />
          <span className="text-[8px] text-muted-foreground truncate w-full text-center leading-tight mt-0.5">{d.label}</span>
        </div>
      ))}
    </div>
  );
}

function NudgeDialog({ card, onOpenChange, onGoCards }) {
  const [sending, setSending] = useState(false);
  if (!card) return null;
  const send = async () => {
    setSending(true);
    try {
      await api.post("/cards/send", { person: card.person });
      toast.success(`Card emailed to ${card.person}`);
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send card");
    } finally { setSending(false); }
  };
  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm" data-testid="resource-nudge-dialog">
        <DialogHeader><DialogTitle className="font-display">{card.person}</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground -mt-1" data-testid="nudge-summary">
          {card.v} open task{card.v === 1 ? "" : "s"} · {card.actions} action item{card.actions === 1 ? "" : "s"}
          {card.overdue ? <span className="font-semibold text-red-600"> · {card.overdue} overdue</span> : null}
        </p>
        {!card.email && <p className="text-[11px] text-amber-600">No email on file — add one on the Cards page first.</p>}
        <div className="flex gap-2 justify-end">
          <Button variant="outline" size="sm" onClick={onGoCards} data-testid="nudge-open-cards">Open Cards</Button>
          <Button size="sm" className="gap-1.5" onClick={send} disabled={sending || !card.email} data-testid="nudge-send-card">
            <Send className="h-3.5 w-3.5" /> {sending ? "Sending…" : "Email their card"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function MiniBars({ data, color, testid }) {
  if (!data.length) data = Array.from({ length: 6 }, () => ({ label: "No resources", v: 0 }));
  const max = Math.max(...data.map((d) => d.v), 1);
  return (
    <div className="flex items-end gap-[3px] h-8 mt-auto" data-testid={testid}>
      {data.slice(0, 8).map((d, i) => (
        <div key={i} title={`${d.label}: ${d.v}`} className="flex-1 rounded-sm min-w-[4px]" style={{ height: `${d.v ? Math.max((d.v / max) * 100, 14) : 5}%`, background: d.v ? color : "#E2E8F0" }} />
      ))}
    </div>
  );
}

function MiniStack({ done, total }) {
  const pctDone = total ? (done / total) * 100 : 0;
  return (
    <div className="mt-auto" data-testid="kpi-mini-stack">
      <div className="h-2 rounded-full bg-blue-100 overflow-hidden">
        <div className="h-full rounded-full" style={{ width: `${pctDone}%`, background: "#059669" }} />
      </div>
      <div className="flex justify-between text-[8px] text-muted-foreground mt-0.5"><span>{done} done</span><span>{Math.max(total - done, 0)} open</span></div>
    </div>
  );
}

function MiniRing({ pct, color, size = 36, showLabel }) {
  const r = 16, c = 2 * Math.PI * r;
  return (
    <svg viewBox="0 0 40 40" className="mt-auto" style={{ height: size, width: size }} data-testid="kpi-mini-ring">
      <circle cx="20" cy="20" r={r} fill="none" stroke="#E2E8F0" strokeWidth="6" />
      <circle cx="20" cy="20" r={r} fill="none" stroke={color} strokeWidth="6" strokeDasharray={`${(Math.min(pct, 100) / 100) * c} ${c}`} strokeLinecap="round" transform="rotate(-90 20 20)" />
      {showLabel && <text x="20" y="24" textAnchor="middle" fontSize="11" fontWeight="800" fill={color}>{pct}%</text>}
    </svg>
  );
}

function MiniPie({ slices, size = 56, testid }) {
  const segments = useMemo(() => {
    const total = slices.reduce((a, x) => a + x.v, 0);
    if (!total) return null;
    let acc = 0;
    return slices.filter((x) => x.v > 0).map((x) => {
      const dash = (x.v / total) * 100;
      const off = -acc;
      acc += dash;
      return { ...x, dash, off };
    });
  }, [slices]);
  if (!segments) return <MiniRing pct={0} color="#10B981" size={size} />;
  return (
    <svg viewBox="0 0 42 42" className="mt-auto -rotate-90" style={{ height: size, width: size }} data-testid={testid}>
      {segments.map((x) => (
        <circle key={x.label} cx="21" cy="21" r="10.5" fill="none" stroke={x.color} strokeWidth="21"
          strokeDasharray={`${x.dash} ${100 - x.dash}`} strokeDashoffset={x.off} pathLength="100">
          <title>{`${x.label}: ${x.v}`}</title>
        </circle>
      ))}
    </svg>
  );
}

function MiniSplit({ a, b, colorA, colorB, labelA, labelB, testid }) {
  const total = a + b;
  const pctA = total ? (a / total) * 100 : 0;
  return (
    <div className="mt-auto" data-testid={testid}>
      <div className="h-2.5 rounded-full overflow-hidden flex" style={{ background: colorB + "33" }}>
        <div className="h-full" style={{ width: `${pctA}%`, background: colorA }} />
        <div className="h-full flex-1" style={{ background: colorB }} />
      </div>
      <div className="flex justify-between text-[8px] text-muted-foreground mt-0.5"><span>{a} {labelA}</span><span>{b} {labelB}</span></div>
    </div>
  );
}

const AUTO_INFO = [
  { icon: Zap, color: "#0044FF", title: "Auto Pilot", body: "Turn it on under \"Auto Pilot & forecasts\". On each run it advances task percent-complete based on the pace each task should move at, flags tasks predicted to finish late, and raises alerts when a blocking decision or slow task puts your dates at risk. You stay in control — true-ups confirm actual progress." },
  { icon: CalendarClock, color: "#A21CAF", title: "Pilot forecast", body: "A live list of predicted finish dates per task. Anything predicted late is flagged in red so you can act before the slip happens instead of after." },
  { icon: IdCard, color: "#7C3AED", title: "Card emails", body: "Every person with open tasks or action items gets a personal \"card\" email on the cadence you pick in the Cards tab (daily, weekly…). Their link lets them submit % updates without logging in — updates land back here for you to confirm." },
  { icon: Send, color: "#F59E0B", title: "Nudges", body: "Owners of due and overdue work get automatic reminders, so you don't spend your week chasing status." },
  { icon: Timer, color: "#10B981", title: "Time saved", body: "The badge estimates PM time returned to you over the last 7 days: ~4 min per auto-rescheduled task, ~10 min per nudge, ~12 min per card email." },
];

function AutomationInfoDialog({ open, onOpenChange }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="automation-info-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><Sparkles className="h-4 w-4 text-[#0044FF]" /> How Automation works</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-muted-foreground -mt-2">PlanIt Orchestrator quietly handles the routine follow-up work of project management. Here's what runs in the background:</p>
        <div className="max-h-[55vh] overflow-y-auto space-y-3 -mx-1 px-1">
          {AUTO_INFO.map((s) => (
            <div key={s.title} className="flex items-start gap-3 rounded-lg border border-border px-3 py-2.5" data-testid={`auto-info-${s.title.toLowerCase().replace(/\s/g, "-")}`}>
              <span className="h-7 w-7 rounded-md flex items-center justify-center shrink-0 mt-0.5" style={{ background: `${s.color}1a` }}>
                <s.icon className="h-4 w-4" style={{ color: s.color }} />
              </span>
              <div className="min-w-0">
                <p className="text-sm font-bold">{s.title}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{s.body}</p>
              </div>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function WeekTodoDialog({ open, onOpenChange, items, multi, onOpenItem, onChanged }) {
  const [doneIds, setDoneIds] = useState([]);
  useEffect(() => { if (open) setDoneIds([]); }, [open]);

  const markDone = async (t) => {
    try {
      await api.patch(`/tasks/${t.id}/status`, { status: "done" });
      setDoneIds((s) => [...s, t.id]);
      toast.success(`"${t.title}" marked done`);
      onChanged?.();
    } catch { toast.error("Could not update task"); }
  };

  const overdue = items.filter((t) => t.kind === "overdue");
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="week-todo-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">This week's to-do list</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-muted-foreground -mt-2" data-testid="week-todo-summary">
          {items.length} step{items.length === 1 ? "" : "s"} to stay on track{overdue.length ? ` · ${overdue.length} overdue` : ""}
        </p>
        <div className="max-h-[55vh] overflow-y-auto -mx-1 px-1 space-y-1" data-testid="week-todo-list">
          {items.length === 0 && <Empty text="Nothing due this week — you're all caught up." />}
          {items.map((t) => {
            const done = doneIds.includes(t.id);
            return (
              <div key={`${t.project_id}-${t.id}`} className={`flex items-start gap-2.5 rounded-lg border border-border px-3 py-2 transition-opacity ${done ? "opacity-50" : "hover:bg-secondary/40"}`} data-testid={`week-todo-item-${t.id}`}>
                <button onClick={() => !done && markDone(t)} className="mt-0.5 shrink-0 text-muted-foreground hover:text-emerald-600 transition-colors" title="Mark done" data-testid={`week-todo-done-${t.id}`}>
                  {done ? <CheckCircle2 className="h-[18px] w-[18px] text-emerald-600" /> : <Circle className="h-[18px] w-[18px]" />}
                </button>
                <button onClick={() => onOpenItem(t)} className="flex-1 min-w-0 text-left group" data-testid={`week-todo-open-${t.id}`}>
                  <span className={`block text-sm font-semibold truncate group-hover:text-primary transition-colors ${done ? "line-through" : ""}`}>{t.title}</span>
                  <span className="block text-[11px] text-muted-foreground truncate">
                    {t.assignee ? `${t.assignee} · ` : ""}{t.percent_complete}% done{t.project_name ? ` · ${t.project_name}` : ""}
                  </span>
                </button>
                <span className={`shrink-0 text-[10px] font-bold px-2 py-0.5 rounded-full mt-0.5 ${t.kind === "overdue" ? "bg-red-800 text-white" : "bg-blue-100 text-blue-700"}`}>
                  {t.kind === "overdue" ? `Overdue · ${t.end_date || ""}` : `Due ${t.end_date || "soon"}`}
                </span>
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function QuickAddDialog({ kind, projectId, onOpenChange, onCreated }) {
  const [form, setForm] = useState({ title: "", owner: "", due_date: "", probability: 3, impact: 3 });
  useEffect(() => { setForm({ title: "", owner: "", due_date: "", probability: 3, impact: 3 }); }, [kind]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (kind === "action") {
        await api.post(`/projects/${projectId}/action-items`, { title: form.title, owner: form.owner, due_date: form.due_date || null });
        toast.success("Action item added");
      } else {
        await api.post(`/projects/${projectId}/risks`, { title: form.title, owner: form.owner, probability: Number(form.probability), impact: Number(form.impact) });
        toast.success("Risk added");
      }
      onCreated();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not add");
    }
  };

  return (
    <Dialog open={!!kind} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="dash-quick-add-dialog">
        <DialogHeader><DialogTitle className="font-display">{kind === "action" ? "New action item" : "New risk"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label htmlFor="qa-title">Title</Label>
            <Input id="qa-title" required value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} className="mt-1.5" data-testid="dash-qa-title" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="qa-owner">Owner</Label>
              <Input id="qa-owner" value={form.owner} onChange={(e) => setForm((f) => ({ ...f, owner: e.target.value }))} className="mt-1.5" data-testid="dash-qa-owner" />
            </div>
            {kind === "action" ? (
              <div>
                <Label htmlFor="qa-due">Due date</Label>
                <Input id="qa-due" type="date" value={form.due_date} onChange={(e) => setForm((f) => ({ ...f, due_date: e.target.value }))} className="mt-1.5" data-testid="dash-qa-due" />
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label htmlFor="qa-prob">P (1-5)</Label>
                  <Input id="qa-prob" type="number" min="1" max="5" value={form.probability} onChange={(e) => setForm((f) => ({ ...f, probability: e.target.value }))} className="mt-1.5" data-testid="dash-qa-prob" />
                </div>
                <div>
                  <Label htmlFor="qa-imp">I (1-5)</Label>
                  <Input id="qa-imp" type="number" min="1" max="5" value={form.impact} onChange={(e) => setForm((f) => ({ ...f, impact: e.target.value }))} className="mt-1.5" data-testid="dash-qa-impact" />
                </div>
              </div>
            )}
          </div>
          <div className="flex justify-end"><Button type="submit" data-testid="dash-qa-submit">Add</Button></div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

const GLANCE_WEDGES = [
  { key: "tasks", title: "Project Task Tracker", tab: "plan", color: "#2563EB", text: "#2563EB", icon: ClipboardList },
  { key: "resources", title: "Resource Tracker", tab: "cards", color: "#7C3AED", text: "#7C3AED", icon: IdCard },
  { key: "raid", title: "RAID Tracker", tab: "raid", color: "#EFD511", text: "#A38F00", icon: ShieldAlert },
  { key: "audit", title: "Audit Tracker", tab: "audit", color: "#10B981", text: "#059669", icon: ShieldCheck },
];
const RAG = { red: "#DC2626", yellow: "#D97706", green: "#059669" };
const ragColorFor = (key, agg) => {
  if (!agg) return null;
  if (key === "tasks") return agg.overdue > 0 ? RAG.red : ((agg.atRisk > 0 || agg.trueupDue) ? RAG.yellow : RAG.green);
  if (key === "resources") return agg.cardsOverdue > 0 ? RAG.red : RAG.green;
  if (key === "raid") {
    const r = agg.raid || {};
    return (r.late_actions || r.blocking_decisions) ? RAG.red : ((r.open_risks || r.open_issues) ? RAG.yellow : RAG.green);
  }
  if (key === "audit") return agg.auditScore < 50 ? RAG.red : ((agg.auditFailing > 0 || agg.auditScore < 100) ? RAG.yellow : RAG.green);
  return null;
};
const GG = { cx: 10, cy: 200, R: 190, connR: 198, iconR: 148, h: 412, yOff: 6, xOff: 130, connLen: 90 };
const wedgeMid = (i) => (-67.5 + i * 45) * Math.PI / 180;
const RAIL_COLORS = ["#EC4899", "#14B8A6", "#0891B2", "#D946EF", "#84CC16", "#F43F5E", "#EA580C", "#0EA5E9"];
const HEALTH_META = {
  on_track: { label: "On track", color: "#10B981" },
  at_risk: { label: "At risk", color: "#CA8A04" },
  off_track: { label: "Off track", color: "#B91C1C" },
  completed: { label: "Completed", color: "#64748B" },
};

function ProjectGlance({ agg, multi, projects, onNavTab, onOpenTrueup, onOpenTodo, onBullet }) {

  const trackers = [
    {
      key: "tasks", title: "Project Task Tracker", icon: ClipboardList, tab: "plan",
      bullets: [
        { kind: "tasks-due", text: `${agg.dueWeek} due this week · ${agg.overdue} overdue · ${agg.atRisk} at risk` },
        { kind: "trueup", text: agg.trueupDue
          ? `True-up due now · ${agg.trueupPending} awaiting confirmation`
          : `True-up next ${agg.trueupNext ? new Date(agg.trueupNext).toLocaleDateString() : "—"}${agg.trueupSubmitted ? ` · ${agg.trueupSubmitted} owner update(s) to review` : ""}` },
      ],
      action: agg.trueupDue && onOpenTrueup ? { label: "Start true-up", fn: onOpenTrueup } : null,
    },
    {
      key: "resources", title: "Resource Tracker", icon: IdCard, tab: "cards",
      bullets: [
        { kind: "cards", text: `Card management — ${agg.cardsOpen} people with open items${agg.cardsOverdue ? ` (${agg.cardsOverdue} with overdue)` : ""}` },
        { kind: "stakeholders", text: "Stakeholder analysis & influence map" },
      ],
    },
    {
      key: "raid", title: "RAID Tracker", icon: ShieldAlert, tab: "raid",
      bullets: [
        { kind: "risks", text: `${agg.raid.open_risks || 0} risks · ${agg.raid.open_issues || 0} issues` },
        { kind: "actions", text: `${agg.raid.open_actions || 0} action items${agg.raid.late_actions ? ` (${agg.raid.late_actions} late)` : ""}` },
        { kind: "decisions", text: `${agg.raid.open_decisions || 0} decisions${agg.raid.blocking_decisions ? ` · ${agg.raid.blocking_decisions} blocking progress` : ""}` },
      ],
    },
    {
      key: "audit", title: "Audit Tracker", icon: ShieldCheck, tab: "audit",
      bullets: [
        { kind: "audit", text: `Compliance ${agg.auditScore}%` },
        { kind: "audit", text: `${agg.auditFailing} standard(s) at risk` },
      ],
    },
  ];

  return (
    <div className="rounded-2xl bg-white p-2 sm:p-4" data-testid="project-glance">
        <div className="self-center min-w-0">
          {/* Desktop: donut + connector lines + wedge-aligned topics */}
          <div className="hidden lg:block relative w-full max-w-[950px] mx-auto" data-testid="glance-circle">
            <GlanceDonut vbW={800} connectors onNavTab={onNavTab} onOpenTodo={onOpenTodo} agg={agg} multi={multi} railCount={(projects || []).length} />
            {trackers.map((tr, i) => {
              const mid = wedgeMid(i);
              const topPct = (GG.cy + GG.connR * Math.sin(mid) + GG.yOff) / GG.h * 100;
              const leftPct = (GG.cx + GG.connR * Math.cos(mid) + GG.connLen + 14 + GG.xOff) / 800 * 100;
              return (
                <button key={tr.key} onClick={() => onNavTab?.(tr.tab)} className="absolute -translate-y-1/2 flex items-start gap-3 text-left group" style={{ left: `${leftPct}%`, top: `${topPct}%`, width: `${97 - leftPct}%` }} data-testid={`glance-tracker-${tr.key}`}>
                  <span className="font-display text-2xl font-extrabold shrink-0 leading-none mt-0.5" style={{ color: ragColorFor(tr.key, agg) || GLANCE_WEDGES[i].text }}>{String.fromCharCode(65 + i)}</span>
                  <TrackerBody tr={tr} onBullet={onBullet} />
                </button>
              );
            })}
          </div>
          {/* Mobile: donut + stacked topics */}
          <div className="lg:hidden" data-testid="glance-circle-mobile">
            <div className="relative w-full max-w-[520px] mx-auto">
              <GlanceDonut vbW={372} onNavTab={onNavTab} onOpenTodo={onOpenTodo} agg={agg} multi={multi} railCount={(projects || []).length} />
            </div>
            <div className="mt-5 space-y-4" data-testid="glance-trackers">
              {trackers.map((tr, i) => (
                <button key={tr.key} onClick={() => onNavTab?.(tr.tab)} className="flex items-start gap-3 text-left group w-full" data-testid={`glance-tracker-m-${tr.key}`}>
                  <span className="font-display text-2xl font-extrabold shrink-0 leading-none mt-0.5" style={{ color: ragColorFor(tr.key, agg) || GLANCE_WEDGES[i].text }}>{String.fromCharCode(65 + i)}</span>
                  <TrackerBody tr={tr} onBullet={onBullet} />
                </button>
              ))}
            </div>
          </div>
        </div>
    </div>
  );
}

function TrackerBody({ tr, onBullet }) {
  return (
    <span className="min-w-0">
      <span className="block font-display font-extrabold text-slate-800 tracking-wide uppercase text-sm group-hover:text-primary transition-colors">{tr.title}</span>
      {tr.bullets.map((b, j) => (
        <span key={j}
          onClick={(e) => { if (b.kind) { e.stopPropagation(); onBullet?.(b.kind); } }}
          title={b.kind ? "Click for a summarized list" : undefined}
          className={`block text-xs text-slate-600 font-medium mt-0.5 ${b.kind ? "cursor-pointer hover:text-primary hover:underline underline-offset-2 transition-colors" : ""}`}
          data-testid={`glance-bullet-${tr.key}-${j}`}>• {b.text}</span>
      ))}
      {tr.action && (
        <span onClick={(e) => { e.stopPropagation(); tr.action.fn?.(); }} className="inline-block mt-1 text-[11px] font-bold text-amber-600 hover:underline" data-testid="glance-start-trueup">→ {tr.action.label}</span>
      )}
    </span>
  );
}

function GlanceListDialog({ onOpenChange, content }) {
  if (!content) return null;
  return (
    <Dialog open onOpenChange={(v) => !v && onOpenChange(null)}>
      <DialogContent className="max-w-lg" data-testid="glance-list-dialog">
        <DialogHeader><DialogTitle className="font-display">{content.title}</DialogTitle></DialogHeader>
        {content.subtitle && <p className="text-xs text-muted-foreground -mt-2" data-testid="glance-list-subtitle">{content.subtitle}</p>}
        <div className="max-h-[55vh] overflow-y-auto -mx-1 px-1 space-y-1" data-testid="glance-list-rows">
          {content.rows.length === 0 && <Empty text={content.empty || "Nothing here right now."} />}
          {content.rows.map((r) => (
            <button key={r.key} onClick={r.onClick} className="w-full flex items-start gap-2.5 rounded-lg border border-border px-3 py-2 text-left hover:bg-secondary/40 transition-colors group" data-testid={`glance-list-row-${r.key}`}>
              <span className="flex-1 min-w-0">
                <span className="block text-sm font-semibold truncate group-hover:text-primary transition-colors">{r.title}</span>
                {r.meta && <span className="block text-[11px] text-muted-foreground truncate">{r.meta}</span>}
              </span>
              {r.badge && <span className={`shrink-0 text-[10px] font-bold px-2 py-0.5 rounded-full mt-0.5 ${r.badgeClass}`}>{r.badge}</span>}
            </button>
          ))}
        </div>
        {content.action && (
          <div className="flex justify-end">
            <Button size="sm" onClick={() => { onOpenChange(null); content.action.fn?.(); }} data-testid="glance-list-action">{content.action.label}</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function GlanceDonut({ vbW, connectors, onNavTab, onOpenTodo, agg, multi, railCount }) {
  const { cx, cy, R, connR, iconR, h, yOff, xOff, connLen } = GG;
  const wide = vbW > 500;
  return (
    <>
      <svg viewBox={`-${xOff} -${yOff} ${vbW} ${h}`} className="w-full h-auto">
        <defs>
          <filter id={`glanceShadow-${vbW}`} x="-40%" y="-40%" width="180%" height="180%">
            <feDropShadow dx="0" dy="10" stdDeviation="14" floodColor="#0F2547" floodOpacity="0.22" />
          </filter>
        </defs>
        {GLANCE_WEDGES.map((w, i) => {
          const a0 = (-90 + i * 45) * Math.PI / 180;
          const a1 = (-45 + i * 45) * Math.PI / 180;
          const p = `M ${cx} ${cy} L ${cx + R * Math.cos(a0)} ${cy + R * Math.sin(a0)} A ${R} ${R} 0 0 1 ${cx + R * Math.cos(a1)} ${cy + R * Math.sin(a1)} Z`;
          return <path key={w.key} d={p} fill={ragColorFor(w.key, agg) || w.color} className="cursor-pointer transition-opacity hover:opacity-85" onClick={() => onNavTab?.(w.tab)} data-testid={`glance-wedge-${w.key}`} />;
        })}
        {connectors && GLANCE_WEDGES.map((w, i) => {
          const mid = wedgeMid(i);
          const xe = cx + connR * Math.cos(mid), ye = cy + connR * Math.sin(mid);
          return (
            <g key={`conn-${w.key}`} data-testid={`glance-connector-${w.key}`}>
              <line x1={xe} y1={ye} x2={xe + connLen} y2={ye} stroke={w.color} strokeWidth="2" />
              <circle cx={xe + connLen} cy={ye} r="4.5" fill={w.color} />
            </g>
          );
        })}
        <circle cx={cx} cy={cy} r="120" fill="#FFFFFF" filter={`url(#glanceShadow-${vbW})`} />
      </svg>
      {/* wedge icons */}
      {GLANCE_WEDGES.map((w, i) => {
        const mid = wedgeMid(i);
        const x = (cx + iconR * Math.cos(mid) + xOff) / vbW * 100;
        const y = (cy + yOff + iconR * Math.sin(mid)) / h * 100;
        return (
          <button key={w.key} onClick={() => onNavTab?.(w.tab)} className="absolute -translate-x-1/2 -translate-y-1/2 text-white/95 hover:scale-110 transition-transform" style={{ left: `${x}%`, top: `${y}%` }} title={w.title} data-testid={`glance-wedge-icon-${w.key}`}>
            <w.icon className={wide ? "h-6 w-6 xl:h-7 xl:w-7" : "h-7 w-7"} strokeWidth={1.6} />
          </button>
        );
      })}
      {/* center heading */}
      <div className="absolute -translate-x-1/2 -translate-y-1/2 text-center" style={{ left: `${(cx + xOff) / vbW * 100}%`, top: "50%", width: `${240 / vbW * 80}%` }}>
        <p className={`font-display font-extrabold text-slate-800 leading-tight ${wide ? "text-lg xl:text-2xl" : "text-xl sm:text-2xl"}`}>Project at a Glance</p>
        <div className="h-px w-16 bg-slate-300 mx-auto my-2" />
        <p className="text-[10px] uppercase tracking-[0.25em] text-slate-400">Steps this week</p>
        <button onClick={onOpenTodo} title="See what needs to be done this week" className="group/todo inline-flex flex-col items-center cursor-pointer" data-testid="glance-week-count">
          <span className={`font-display font-extrabold text-primary transition-transform group-hover/todo:scale-110 ${wide ? "text-2xl xl:text-3xl" : "text-3xl"}`}>{agg.dueWeek + agg.overdue}</span>
          <span className="text-[9px] font-bold uppercase tracking-wide text-primary/70 opacity-0 group-hover/todo:opacity-100 transition-opacity -mt-0.5">View to-do list</span>
        </button>
        <p className="text-[10px] text-slate-400">{agg.pct}% complete{multi ? ` · ${railCount} projects` : ""}</p>
      </div>
    </>
  );
}

function Sparkline({ points, color, width = 150, height = 34 }) {
  if (points.length < 2) return <span className="text-[10px] text-muted-foreground italic">history builds daily</span>;
  const vals = points.map((p) => p.avg_percent);
  const min = Math.min(...vals), max = Math.max(...vals);
  const span = Math.max(max - min, 1);
  const step = width / (points.length - 1);
  const pts = points.map((p, i) => `${(i * step).toFixed(1)},${(height - 3 - ((p.avg_percent - min) / span) * (height - 8)).toFixed(1)}`).join(" ");
  const last = pts.split(" ").pop().split(",");
  return (
    <svg width={width} height={height} className="shrink-0">
      <polyline points={pts} fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
      <circle cx={last[0]} cy={last[1]} r="2.5" fill={color} />
    </svg>
  );
}

function TrendHistoryDialog({ open, onOpenChange, projectIds, projects, overallPct, overallDelta }) {
  const [rows, setRows] = useState(null);
  const [nudging, setNudging] = useState(null);
  const [nudged, setNudged] = useState([]);
  const sendStalledNudge = async (r) => {
    setNudging(r.id);
    try {
      const { data } = await api.post(`/projects/${r.id}/nudge-stalled`);
      if (data.sent) {
        toast.success(`Check-in nudge emailed to ${data.sent} ${data.sent === 1 ? "person" : "people"} on ${r.name}`);
        setNudged((s) => [...s, r.id]);
      } else {
        toast.warning(data.skipped ? "No one on this project has an email yet — add emails on the Cards page." : "No one with open items to nudge.");
      }
    } catch { toast.error("Could not send nudges"); }
    finally { setNudging(null); }
  };
  useEffect(() => {
    if (!open) { setRows(null); return; }
    (async () => {
      try {
        const out = await Promise.all(projectIds.map(async (pid) => {
          const { data } = await api.get(`/projects/${pid}/progress-history`);
          const h = data.history || [];
          const cur = h.length ? h[h.length - 1].avg_percent : 0;
          const weekAgo = new Date(Date.now() - 7 * 86400000).toISOString().slice(0, 10);
          let base = null;
          h.forEach((p) => { if (p.date <= weekAgo) base = p; });
          if (!base && h.length > 1) base = h[0];
          const delta = base ? cur - base.avg_percent : null;
          return { id: pid, name: projects.find((x) => x.id === pid)?.name || "Project", history: h, cur, delta,
            stalled: !!data.stalled, stalledDays: data.stalled_days || 0 };
        }));
        out.sort((a, b) => (a.delta ?? 0) - (b.delta ?? 0));
        setRows(out);
      } catch { toast.error("Could not load progress history"); }
    })();
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="dash-trend-dialog">
        <DialogHeader><DialogTitle className="font-display">Week-over-week progress</DialogTitle></DialogHeader>
        <p className="text-xs text-muted-foreground -mt-2" data-testid="dash-trend-summary">
          Overall {overallPct}% complete{overallDelta != null ? (overallDelta > 0 ? ` · up ${overallDelta} pts vs last week` : overallDelta < 0 ? ` · down ${Math.abs(overallDelta)} pts vs last week` : " · unchanged vs last week") : ""} — slowest movers first.
        </p>
        <div className="max-h-[55vh] overflow-y-auto -mx-1 px-1 space-y-1">
          {!rows && <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>}
          {rows && rows.map((r) => {
            const color = r.delta > 0 ? "#059669" : r.delta < 0 ? "#DC2626" : "#64748B";
            return (
              <div key={r.id} className={`rounded-lg border px-3 py-2 ${r.stalled ? "border-amber-300 bg-amber-50/60" : "border-border"}`} data-testid={`trend-row-${r.id}`}>
                <div className="flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold truncate">{r.name}</p>
                    <p className="text-[11px] text-muted-foreground">{r.cur}% complete</p>
                  </div>
                  <Sparkline points={r.history} color={color} />
                  {r.stalled && <span className="shrink-0 text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-500 text-white" data-testid={`trend-stalled-${r.id}`}>Stalled {r.stalledDays}d</span>}
                  <span className={`shrink-0 text-[11px] font-bold px-2 py-0.5 rounded-full tabular-nums ${r.delta > 0 ? "bg-emerald-100 text-emerald-700" : r.delta < 0 ? "bg-red-100 text-red-600" : "bg-secondary text-muted-foreground"}`}>
                    {r.delta == null ? "new" : r.delta > 0 ? `▲ +${r.delta}` : r.delta < 0 ? `▼ ${r.delta}` : "— 0"}
                  </span>
                </div>
                {r.stalled && (
                  <div className="flex items-center gap-2 mt-1.5 pt-1.5 border-t border-amber-200">
                    <Sparkles className="h-3 w-3 text-amber-600 shrink-0" />
                    <p className="text-[11px] text-amber-800 flex-1">Auto Pilot: progress flat for {r.stalledDays} days — send everyone with open items a check-in card?</p>
                    <Button size="sm" variant="outline" className="h-6 px-2 text-[11px] gap-1 border-amber-400 text-amber-800 hover:bg-amber-100" disabled={nudging === r.id || nudged.includes(r.id)} onClick={() => sendStalledNudge(r)} data-testid={`trend-nudge-${r.id}`}>
                      <Send className="h-3 w-3" /> {nudged.includes(r.id) ? "Sent" : nudging === r.id ? "Sending…" : "Send nudges"}
                    </Button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}
